import { useState, useEffect, useRef } from "react";
import { PropertyDocuments } from "./PropertyDocuments";
import PropertyHistoryTab from "./PropertyHistoryTab";
import AddressAutocomplete from "../ui/AddressAutocomplete";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SuburbIntelligenceSection } from "./SuburbIntelligenceSection";
import { CollapsibleSection as CollapsiblePanel } from "../ui/CollapsibleSection";
import { computePropertyNotices } from "../../lib/computeNoticeBoard";
import { loadAppSettings } from "../settings/SettingsPage";
import { loadProfile } from "../../lib/storage";
import { NoticeItem } from "../dashboard/NoticeItem";
import { EmailComposeModal } from "../ui/EmailComposeModal";
import type { NoticeBoardItem } from "../../types/noticeboard";
import { NumberInput } from "../ui/NumberInput";
import { useHtagData } from "../../lib/useHtagData";
import { getHtagType } from "../../lib/htag-property-type";
import { InfoTooltip } from "../ui/InfoTooltip";
import { toAnnual, filterDeductibleExpenseRows, isExcludedExpenseCategory, propertyMetrics, annualLoanInterest, annualLoanRepayment, personalTaxSavingFromLoss, totalLoanBalance, lvr, isPersonalProperty, cgtDiscountRate, isHeldOverTwelveMonths, computeDiv40Eligibility, computeDiv40BalancingAdjustment, type Div40EligibilityProperty, type Div40EligibilityOwnerStructure } from "../../lib/calculations";
import { recomputeLowValuePools, fyLabelFromDate } from "../../lib/lowValuePool";
import { deriveCoOwnersForMode, type OwnershipMode } from "../../lib/householdScope";
import { callAi, aiErrorMessage, type AiCallError } from "../../lib/aiCall";
import { isSupabaseEnabled, supabase } from "../../lib/supabase";
import { uploadToStorage, downloadFromStorage, deleteFromStorage } from "../../lib/userStorage";
import { migratePropertyDocs } from "../../lib/docMigration";
import type {
  PropertyRecord,
  AcquisitionCostRow,
  ExpenseRow,
  IncomeRow,
  InsurancePolicy,
  PropertyDocument,
  ChangeLogEntry,
  Div40Item,
} from "../../types/portfolio";
import type { Structure, InvestorProfile } from "../../types/entities";
import type { PropertyAssetClass, PropertyStyle } from "../../types/entities";
import type { SubscriptionTier } from "../billing/BillingPage";
import { geocodeAddress } from "../../lib/geocode";

// ─── Constants ────────────────────────────────────────────────────────────────

const ASSET_CLASSES: PropertyAssetClass[] = [
  "Residential", "Commercial", "Industrial", "Mixed Use",
];
const PROPERTY_STYLES: PropertyStyle[] = [
  "House", "House + Granny Flat", "Townhouse", "Unit", "Duplex",
  "Triplex", "Apartment", "Villa", "Acreage", "Retail", "Office",
  "Warehouse", "Other",
];
const INSURANCE_TYPES = [
  "Landlord", "Building", "Contents", "Public Liability", "Flood", "Other",
];
const DOC_TAGS = [
  "contract", "lease", "insurance", "invoice", "rates", "correspondence", "other",
] as const;
const FREQ_LABELS: Record<string, string> = {
  weekly: "W", fortnightly: "F", monthly: "M", yearly: "Y",
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

type RowFreq = "weekly" | "fortnightly" | "monthly" | "yearly";

function toFortnight(amount: number, freq: RowFreq): number {
  return toAnnual(amount, freq) / 26;
}

function daysUntil(dateStr: string): number {
  return Math.ceil((Date.parse(dateStr) - Date.now()) / 86_400_000);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function fmt(n: number): string {
  return "$" + Math.round(n).toLocaleString("en-AU");
}

// Compact dollar formatter for tight subtext (e.g. multi-loan breakdown pills).
// Use fmt() for primary figures; reserve fmtCompact for narrow secondary lines.
function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  return `$${Math.round(n / 1000)}k`;
}

function pct(n: number): string {
  return n.toFixed(2) + "%";
}


// ─── Default row builders ─────────────────────────────────────────────────────

function defaultExpenseRows(p: PropertyRecord): ExpenseRow[] {
  if (p.expenseRows && p.expenseRows.length > 0) return p.expenseRows;
  // "Mortgage repayments" is intentionally NOT in this list. Loan costs
  // belong on the Loans tab — the loan_balance × rate is the source of
  // truth for interest. Adding mortgage repayments here would double-count
  // interest and incorrectly expense the principal. See calculations.ts
  // EXCLUDED_EXPENSE_CATEGORIES.
  return [
    { id: uid(), category: "Agent management fee",  amount: p.annualPropertyManagement ?? 0,  frequency: "yearly"  },
    { id: uid(), category: "Landlord insurance",    amount: p.annualInsurance ?? 0,           frequency: "yearly"  },
    { id: uid(), category: "Land tax",              amount: p.annualLandTax ?? 0,             frequency: "yearly"  },
    { id: uid(), category: "Rates",                 amount: p.annualRates ?? 0,               frequency: "yearly"  },
    { id: uid(), category: "Strata",                amount: p.annualStrata ?? 0,              frequency: "yearly"  },
    { id: uid(), category: "Maintenance",           amount: p.annualMaintenance ?? 0,         frequency: "yearly"  },
    { id: uid(), category: "Water",                 amount: 0,                                 frequency: "yearly"  },
    { id: uid(), category: "Service & trades",      amount: 0,                                 frequency: "yearly"  },
    { id: uid(), category: "Accounting fees",       amount: 0,                                 frequency: "yearly"  },
    { id: uid(), category: "Body corporate",        amount: 0,                                 frequency: "yearly"  },
    { id: uid(), category: "Home insurance",        amount: 0,                                 frequency: "yearly"  },
  ];
}

function defaultIncomeRows(p: PropertyRecord): IncomeRow[] {
  if (p.incomeRows && p.incomeRows.length > 0) return p.incomeRows;
  return [
    { id: uid(), category: "Rental income",             amount: p.weeklyRent ?? 0, frequency: "weekly" },
    { id: uid(), category: "Tax return / depreciation", amount: 0,                 frequency: "yearly" },
  ];
}

function defaultAcquisitionCostRows(p: PropertyRecord): AcquisitionCostRow[] {
  if (p.acquisitionCosts && p.acquisitionCosts.length > 0) return p.acquisitionCosts;
  return [
    { id: uid(), category: "Stamp duty",                amount: p.buyCosts ?? 0       },
    { id: uid(), category: "Legal / conveyancing fees", amount: p.legalBuyCosts ?? 0  },
    { id: uid(), category: "Building inspection",       amount: 0                      },
    { id: uid(), category: "Pest inspection",           amount: 0                      },
    { id: uid(), category: "Loan setup fee",            amount: 0                      },
    { id: uid(), category: "Title search",              amount: 0                      },
    { id: uid(), category: "Council rate adjustments",  amount: 0                      },
    { id: uid(), category: "Other",                     amount: 0                      },
  ];
}

// ─── Alert computation ────────────────────────────────────────────────────────

// Alert type and computeAlerts removed — replaced by computePropertyNotices from
// src/lib/computeNoticeBoard.ts + shared NoticeItem component (N5 Stage 2).

// ─── Small display/input components ──────────────────────────────────────────

function StatPill({ label, value, sub, info }: { label: string; value: string; sub?: string; info?: string }) {
  return (
    <div className="flex flex-col rounded-xl bg-neutral-100 px-4 py-2.5 min-w-[120px]">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500">
        {label}
        {info && <InfoTooltip tip={info} />}
      </span>
      <span className="mt-0.5 text-base font-semibold text-neutral-900">{value}</span>
      {sub && <span className="text-[11px] text-neutral-400">{sub}</span>}
    </div>
  );
}

function EF({
  label, value, onChange, onBlur, type = "text", options, step, disabled, tooltip,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  options?: string[];
  step?: string;
  disabled?: boolean;
  tooltip?: string;
}) {
  const base =
    "w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-neutral-50 disabled:text-neutral-400 disabled:cursor-not-allowed";
  return (
    <div>
      <label className="mb-0.5 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
        {label}
        {tooltip && <InfoTooltip tip={tooltip} />}
      </label>
      {options ? (
        <select className={base} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === "number" ? (
        <NumberInput
          className={base}
          step={step}
          disabled={disabled}
          value={value === 0 || value === "0" ? "" : value}
          placeholder="0"
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : (
        <input
          className={base}
          type={type}
          step={step}
          disabled={disabled}
          value={value}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </div>
  );
}

// ─── Collapsible section ──────────────────────────────────────────────────────

function CollapsibleSection({
  id: _id, title, summary, collapsed, onToggle, children,
}: {
  id: string;
  title: string;
  summary: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-neutral-100 bg-white overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-3.5 cursor-pointer select-none hover:bg-neutral-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-neutral-400 text-xs">{collapsed ? "▶" : "▼"}</span>
          <span className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
            {title}
          </span>
          {collapsed && (
            <span className="ml-2 truncate text-xs text-neutral-500">{summary}</span>
          )}
        </div>
      </div>
      {!collapsed && (
        <div className="border-t border-neutral-100 px-5 py-4">{children}</div>
      )}
    </div>
  );
}

// ─── Acquisition cost table (no frequency — one-off amounts) ─────────────────

function AcquisitionTable({
  rows, onChangeRow, onDeleteRow, onAddRow, onSave,
}: {
  rows: AcquisitionCostRow[];
  onChangeRow: (id: string, field: "category" | "amount", value: string) => void;
  onDeleteRow: (id: string) => void;
  onAddRow: () => void;
  onSave?: () => void;
}) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-100 text-left text-[11px] uppercase tracking-wider text-neutral-400">
            <th className="pb-2 pr-3 font-medium">Item</th>
            <th className="pb-2 pr-3 font-medium w-32 text-right">Amount</th>
            <th className="pb-2 w-6" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-neutral-50 last:border-0">
              <td className="py-1.5 pr-3">
                <input
                  className="w-full rounded border border-neutral-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                  value={row.category}
                  onChange={(e) => onChangeRow(row.id, "category", e.target.value)}
                  onBlur={onSave}
                />
              </td>
              <td className="py-1.5 pr-3 text-right">
                <NumberInput
                  step="0.01"
                  className="w-full rounded border border-neutral-200 px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-400"
                  value={row.amount === 0 ? "" : row.amount}
                  onChange={(e) => onChangeRow(row.id, "amount",
                    e.target.value === "" ? "0" : String(parseFloat(e.target.value) || 0)
                  )}
                  onBlur={onSave}
                />
              </td>
              <td className="py-1.5">
                <button
                  onClick={() => onDeleteRow(row.id)}
                  className="text-neutral-300 hover:text-red-400 text-base leading-none"
                >×</button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-neutral-200 font-semibold text-neutral-800">
            <td className="pt-2 pr-3">Total</td>
            <td className="pt-2 pr-3 text-right">{fmt(total)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <button
        onClick={onAddRow}
        className="mt-3 rounded-lg border border-dashed border-neutral-300 px-4 py-1.5 text-xs text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
      >
        + Add item
      </button>
    </div>
  );
}

// ─── Sortable row wrapper for DnD ─────────────────────────────────────────────

function SortableRow({
  id,
  children,
}: {
  id: string;
  children: (dragHandleProps: React.HTMLAttributes<HTMLElement>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#FDF3E7' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </tr>
  );
}

// ─── Spreadsheet table (expenses / income) ────────────────────────────────────

function SpreadsheetTable({
  rows, onChangeRow, onDeleteRow, onAddRow, onReorder, onSave, addLabel,
}: {
  rows: (ExpenseRow | IncomeRow)[];
  onChangeRow: (id: string, field: "category" | "amount" | "frequency", value: string) => void;
  onDeleteRow: (id: string) => void;
  onAddRow: () => void;
  onReorder?: (newRows: (ExpenseRow | IncomeRow)[]) => void;
  onSave?: () => void;
  addLabel: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      onReorder?.(arrayMove(rows, oldIndex, newIndex));
    }
  }

  const totalAnnual = rows.reduce((s, r) => s + toAnnual(r.amount, r.frequency as RowFreq), 0);
  const totalFn = totalAnnual / 26;
  const isDnd = !!onReorder;

  const rowContent = (row: ExpenseRow | IncomeRow, dragHandleProps?: React.HTMLAttributes<HTMLElement>) => {
    const annual = toAnnual(row.amount, row.frequency as RowFreq);
    const fn = toFortnight(row.amount, row.frequency as RowFreq);
    return (
      <>
        {isDnd && (
          <td className="py-1.5 pr-1 w-5">
            <span
              {...dragHandleProps}
              className="cursor-grab text-neutral-300 hover:text-neutral-500 select-none text-base leading-none"
              title="Drag to reorder"
            >
              ⠿
            </span>
          </td>
        )}
        <td className="py-1.5 pr-3">
          <input
            className="w-full rounded border border-neutral-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={row.category}
            onChange={(e) => onChangeRow(row.id, "category", e.target.value)}
            onBlur={onSave}
          />
        </td>
        <td className="py-1.5 pr-3">
          <NumberInput
            step="0.01"
            className="w-full rounded border border-neutral-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={row.amount === 0 ? "" : row.amount}
            onChange={(e) => onChangeRow(row.id, "amount",
              e.target.value === "" ? "0" : String(parseFloat(e.target.value) || 0)
            )}
            onBlur={onSave}
          />
        </td>
        <td className="py-1.5 pr-3">
          <select
            className="rounded border border-neutral-200 px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={row.frequency}
            onChange={(e) => onChangeRow(row.id, "frequency", e.target.value)}
            onBlur={onSave}
          >
            {Object.entries(FREQ_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </td>
        <td className="py-1.5 pr-3 text-right text-neutral-700">{fmt(annual)}</td>
        <td className="py-1.5 pr-3 text-right text-neutral-500">{fmt(fn)}</td>
        <td className="py-1.5">
          <button
            onClick={() => onDeleteRow(row.id)}
            className="text-neutral-300 hover:text-red-400 text-base leading-none"
          >×</button>
        </td>
      </>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-100 text-left text-[11px] uppercase tracking-wider text-neutral-400">
            {isDnd && <th className="pb-2 w-5" />}
            <th className="pb-2 pr-3 font-medium">Category</th>
            <th className="pb-2 pr-3 font-medium w-32">Amount</th>
            <th className="pb-2 pr-3 font-medium w-24">Freq</th>
            <th className="pb-2 pr-3 font-medium w-32 text-right">Annual</th>
            <th className="pb-2 pr-3 font-medium w-32 text-right">Fortnight</th>
            <th className="pb-2 w-6" />
          </tr>
        </thead>
        {isDnd ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              <tbody>
                {rows.map((row) => (
                  <SortableRow key={row.id} id={row.id}>
                    {(dragHandleProps) => rowContent(row, dragHandleProps)}
                  </SortableRow>
                ))}
              </tbody>
            </SortableContext>
          </DndContext>
        ) : (
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-neutral-50 last:border-0">
                {rowContent(row)}
              </tr>
            ))}
          </tbody>
        )}
        <tfoot>
          <tr className="border-t-2 border-neutral-200 font-semibold text-neutral-800">
            <td className="pt-2 pr-3" colSpan={isDnd ? 4 : 3}>Total</td>
            <td className="pt-2 pr-3 text-right">{fmt(totalAnnual)}</td>
            <td className="pt-2 pr-3 text-right">{fmt(totalFn)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <button
        onClick={onAddRow}
        className="mt-3 rounded-lg border border-dashed border-neutral-300 px-4 py-1.5 text-xs text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
      >
        + {addLabel}
      </button>
    </div>
  );
}

// ─── Documents slide-out panel ────────────────────────────────────────────────

const DOCUMENTS_PANEL_ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/heic", "image/heif"];
const DOCUMENTS_PANEL_MAX_SIZE = 10 * 1024 * 1024; // 10 MB

function DocumentsPanel({
  open, onClose, propertyId, userId, documents, changeLog, onUpload, onUpdateDocTag, onRemoveDoc,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  userId?: string;
  documents: PropertyDocument[];
  changeLog: ChangeLogEntry[];
  onUpload: (doc: PropertyDocument) => void;
  onUpdateDocTag: (id: string, tag: string) => void;
  onRemoveDoc: (id: string) => void;
}) {
  const [filter, setFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const years = Array.from(
    new Set(
      documents.map((d) => {
        const dt = new Date(d.dateUploaded);
        const m = dt.getMonth();
        const y = dt.getFullYear();
        const fyStart = m >= 6 ? y : y - 1;
        return `${fyStart}–${fyStart + 1}`;
      })
    )
  ).sort().reverse();

  const filtered = documents.filter((d) => {
    if (filter !== "all" && d.tag !== filter) return false;
    if (yearFilter !== "all") {
      const dt = new Date(d.dateUploaded);
      const m = dt.getMonth();
      const y = dt.getFullYear();
      const fyStart = m >= 6 ? y : y - 1;
      if (`${fyStart}–${fyStart + 1}` !== yearFilter) return false;
    }
    return true;
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!userId) {
      setUploadError("Sign in required to upload documents.");
      return;
    }
    setUploadError(null);
    setUploading(true);
    for (const file of Array.from(files)) {
      if (!DOCUMENTS_PANEL_ACCEPTED_MIME.includes(file.type)) {
        setUploadError(`Skipped ${file.name} — only PDF/JPG/PNG/HEIC accepted.`);
        continue;
      }
      if (file.size > DOCUMENTS_PANEL_MAX_SIZE) {
        setUploadError(`Skipped ${file.name} — file size must be under 10 MB.`);
        continue;
      }
      // Path: {userId}/{propertyId}/other (default tag; user can change via the tag input)
      const result = await uploadToStorage("property-documents", `${userId}/${propertyId}/other`, file);
      if (!result.ok) {
        setUploadError(result.error);
        continue;
      }
      onUpload({
        id: uid(),
        filename: file.name,
        type: file.type,
        dateUploaded: new Date().toISOString().slice(0, 10),
        filePath: result.result.filePath,
        fileSize: result.result.fileSize,
        needsReupload: false,
        tag: "other",
      });
    }
    setUploading(false);
  }

  const docIcons: Record<string, string> = {
    "application/pdf": "📄",
    "image/jpeg": "🖼️",
    "image/png": "🖼️",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "📝",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "📊",
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/20 transition-opacity duration-300"
        style={{ opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none" }}
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-[42%] min-w-[340px] flex-col bg-white shadow-2xl transition-transform duration-300"
        style={{ transform: open ? "translateX(0)" : "translateX(100%)" }}
      >
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-4">
          <span className="font-semibold text-neutral-900">Documents &amp; History</span>
          <button onClick={onClose} className="text-xl leading-none text-neutral-400 hover:text-neutral-700">×</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div
            className="m-4 cursor-pointer rounded-xl border-2 border-dashed border-neutral-200 px-4 py-6 text-center hover:border-neutral-400"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); void handleFiles(e.dataTransfer.files); }}
          >
            <div className="text-2xl">⬆️</div>
            <p className="mt-1 text-sm text-neutral-500">{uploading ? "Uploading…" : "Drag & drop or click to upload"}</p>
            <p className="text-xs text-neutral-400">PDF, JPG, PNG, HEIC · max 10 MB</p>
            <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
              className="hidden" disabled={uploading} onChange={(e) => void handleFiles(e.target.files)} />
          </div>
          {uploadError && (
            <div className="mx-4 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{uploadError}</div>
          )}

          <div className="flex flex-wrap gap-1.5 px-4 pb-2">
            {["all", ...DOC_TAGS].map((t) => (
              <button key={t} onClick={() => setFilter(t)}
                className={`rounded-full px-3 py-1 text-xs capitalize ${filter === t ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                {t}
              </button>
            ))}
          </div>

          {years.length > 0 && (
            <div className="px-4 pb-3">
              <select className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs text-neutral-600 focus:outline-none"
                value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                <option value="all">All financial years</option>
                {years.map((y) => <option key={y} value={y}>FY {y}</option>)}
              </select>
            </div>
          )}

          <datalist id="doc-tag-suggestions">
            {DOC_TAGS.map((t) => <option key={t} value={t} />)}
          </datalist>

          <div className="space-y-2 px-4 pb-4">
            {filtered.length === 0 && (
              <p className="py-4 text-center text-xs text-neutral-400">No documents yet.</p>
            )}
            {filtered.map((doc) => {
              const lost = doc.needsReupload || (!doc.filePath && !!doc.filename);
              return (
                <div key={doc.id} className={`rounded-xl border p-3 ${lost ? "border-amber-300 bg-amber-50" : "border-neutral-100 bg-neutral-50"}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{docIcons[doc.type] ?? "📎"}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-neutral-800">{doc.filename}</div>
                      <div className="text-xs text-neutral-400">{formatDate(doc.dateUploaded)}</div>
                      {lost && (
                        <div className="mt-0.5 text-xs font-medium text-amber-700">File lost — please re-upload</div>
                      )}
                      <input
                        list="doc-tag-suggestions"
                        className="mt-1 w-36 rounded border border-neutral-200 px-1.5 py-0.5 text-xs text-neutral-600 focus:outline-none"
                        value={doc.tag ?? ""}
                        onChange={(e) => onUpdateDocTag(doc.id, e.target.value)}
                        placeholder="tag…"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      {lost ? (
                        <span className="rounded-lg border border-amber-200 px-2 py-0.5 text-xs text-amber-700">No file</span>
                      ) : doc.filePath ? (
                        <button
                          onClick={() => downloadFromStorage("property-documents", doc.filePath!)}
                          className="rounded-lg border border-neutral-200 px-2 py-0.5 text-xs text-neutral-600 hover:bg-neutral-100"
                        >
                          View
                        </button>
                      ) : null}
                      <button
                        onClick={() => { if (confirm("Remove this document?")) onRemoveDoc(doc.id); }}
                        className="rounded-lg border border-neutral-200 px-2 py-0.5 text-xs text-neutral-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-neutral-100 px-4 pb-6 pt-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-400">Change Log</h4>
            {changeLog.length === 0 && <p className="text-xs text-neutral-400">No changes recorded yet.</p>}
            {[...changeLog].reverse().map((entry) => (
              <div key={entry.id} className="mb-2.5 rounded-xl bg-neutral-50 px-3 py-2 text-xs">
                <div className="font-medium text-neutral-700">{entry.fieldName}</div>
                <div className="mt-0.5 flex items-center gap-1 text-neutral-500">
                  <span className="line-through">{entry.oldValue || "—"}</span>
                  <span>→</span>
                  <span className="text-neutral-700">{entry.newValue || "—"}</span>
                </div>
                <div className="mt-0.5 text-neutral-400">{formatDate(entry.date)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PropertyDetailPage({
  property,
  structures,
  onSave,
  onBack,
  onExportToSell,
  onDeleteProperty,
  investorMarginalRate = 32.5,
  profile,
  userId,
  subscriptionTier,
  onNavigateToBilling,
  onNavigateToProfile,
}: {
  property: PropertyRecord;
  structures: Structure[];
  onSave: (updated: PropertyRecord) => void;
  onBack: () => void;
  onExportToSell?: (id: string) => void;
  onDeleteProperty?: (id: string) => void;
  /** @deprecated Display-only — used in the "at X% marginal rate" label.
   *  Tax-saving math goes through `personalTaxSavingFromLoss(loss, profile)`.
   *  Kept as a fallback when `profile` is not threaded. */
  investorMarginalRate?: number;
  /** Full profile — required for accurate effective-marginal tax-saving math
   *  (bracket MTR + Medicare + MLS + HELP). When undefined, tax-saving display
   *  falls back to nominal `investorMarginalRate × loss`. */
  profile?: InvestorProfile;
  userId?: string;
  subscriptionTier?: SubscriptionTier;
  onNavigateToBilling?: () => void;
  /** B103 Phase 3 — empty-state link from the ownership editor when the user
   *  has no eligible household members and needs to add a partner first. */
  onNavigateToProfile?: () => void;
}) {
  const [draft, setDraft] = useState<PropertyRecord>(() => ({
    ...property,
    marketValue: property.marketValue ?? property.currentValue,
    expenseRows: defaultExpenseRows(property),
    incomeRows: defaultIncomeRows(property),
    acquisitionCosts: property.acquisitionCosts ?? defaultAcquisitionCostRows(property),
    insurancePolicies: property.insurancePolicies ?? [],
    documents: property.documents ?? [],
    changeLog: property.changeLog ?? [],
  }));

  useEffect(() => {
    setDraft({
      ...property,
      marketValue: property.marketValue ?? property.currentValue,
      expenseRows: defaultExpenseRows(property),
      incomeRows: defaultIncomeRows(property),
      acquisitionCosts: property.acquisitionCosts ?? defaultAcquisitionCostRows(property),
      insurancePolicies: property.insurancePolicies ?? [],
      documents: property.documents ?? [],
      changeLog: property.changeLog ?? [],
    });
  }, [property.id]);

  // ── B23 — Property document migration (detect stale blob: URLs from pre-B23) ──
  useEffect(() => {
    const migrated = migratePropertyDocs(property.documents ?? []);
    if (migrated.changed) {
      const updated = { ...property, documents: migrated.documents };
      onSave(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // J7 — Address UX state machine:
  //   Locked (default when address exists): summary line + Edit button only.
  //   Editing (no address yet, OR user clicked Edit): autocomplete +
  //     manual-entry checkbox. Pick from list OR tick to type manually.
  //   Manual mode (checkbox ticked while editing): 4 fields visible, no
  //     autocomplete. Save returns to locked state.
  const hasAddress = !!(draft.address || draft.suburb);
  const [editingAddress, setEditingAddress] = useState(!hasAddress);
  const [manualAddress, setManualAddress] = useState(false);
  const [htagOpen, setHtagOpen] = useState(false);
  const rawPropertyType = draft.propertyType ?? draft.assetClass ?? "house";
  const htagType = getHtagType(rawPropertyType) ?? "house";
  const htagPropertyTypeLabel = htagType === "unit" ? "Units" : "Houses";
  const { data: htagData, loading: htagLoading, error: htagError } = useHtagData(draft.lgaId ?? undefined, htagType, draft.addressKey ?? undefined, htagOpen);
  const [showDocs, setShowDocs] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState<Record<string, number>>({});
  const [emailNotice, setEmailNotice] = useState<NoticeBoardItem | null>(null);
  const [saveError, setSaveError] = useState("");
  // B23 — PM/lease section upload state. Surfaced inline near the buttons.
  const [sectionUploadError, setSectionUploadError] = useState<string | null>(null);
  const [sectionUploading, setSectionUploading] = useState<string | null>(null);
  const draftRef = useRef<PropertyRecord>(draft);
  // Sync ref immediately after draft state is declared — runs on every render
  draftRef.current = draft;
  const [deleteModal, setDeleteModal] = useState(false);
  const [markSoldModal, setMarkSoldModal] = useState(false);
  const [saleFields, setSaleFields] = useState({
    saleDate: draft.saleDate ?? "",
    // B130 Wave 2A — CGT event date per RPG 2025 p62. Mirrors saleDate
    // until Phase 6 form prompts the user to refine where contract and
    // settlement dates differ.
    saleContractDate: draft.saleContractDate ?? "",
    salePrice: draft.salePrice ?? 0,
    saleAgentFees: draft.saleAgentFees ?? 0,
    saleLegalCosts: draft.saleLegalCosts ?? 0,
    saleOtherCosts: draft.saleOtherCosts ?? 0,
    saleNotes: draft.saleNotes ?? "",
  });
  // CGT confirm-sale flow state — saving while the cgt_events insert is in
  // flight, error if it fails so the user can retry or continue without
  // the CGT record (Bug E fix).
  const [cgtSaving, setCgtSaving] = useState(false);
  const [cgtError, setCgtError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<AiCallError | null>(null);
  const [savedToast, setSavedToast] = useState(false);
  const savedToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // B130 Wave 2A Phase 6 — legacy contract-date prompt. Phase 1 backfilled
  // contract_date := purchase_date for rows that had no separate contract
  // date captured. Show a one-time amber banner so the user can refine.
  // Per-property dismiss flag persists in localStorage.
  const [contractPromptDismissed, setContractPromptDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`contractDatePrompt:dismissed:${property.id}`) !== null;
    } catch {
      return true; // SSR / disabled localStorage — suppress
    }
  });

  // Scroll-triggered compact bar — pure DOM class toggle, no React state
  const heroRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // Section-level file upload refs
  const pmFileRef = useRef<HTMLInputElement>(null);
  const leaseFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current || !pageRef.current) return;
      const bottom = heroRef.current.getBoundingClientRect().bottom;
      if (bottom <= 0) {
        pageRef.current.classList.add("hero-hidden");
      } else {
        pageRef.current.classList.remove("hero-hidden");
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  function toggleCollapse(id: string) {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function saveProperty(updatedOrEvent?: PropertyRecord | React.FocusEvent | React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) {
    // Only treat the argument as a PropertyRecord if it has a string id field — otherwise it's a DOM event
    const updated = updatedOrEvent && typeof updatedOrEvent === 'object' && 'id' in updatedOrEvent && typeof (updatedOrEvent as PropertyRecord).id === 'string'
      ? (updatedOrEvent as PropertyRecord)
      : undefined;
    const toSave = updated ?? draftRef.current;
    if (!toSave?.id) return;
    onSave(toSave); // App.tsx updateProperty: updates local state + upserts to Supabase
    setSaveError("");
    if (savedToastTimer.current) clearTimeout(savedToastTimer.current);
    setSavedToast(true);
    savedToastTimer.current = setTimeout(() => setSavedToast(false), 2000);

    // Fire-and-forget geocoding — populate lat/lng the first time address fields are saved
    if (!toSave.latitude && !toSave.longitude && toSave.address && toSave.suburb && toSave.state) {
      geocodeAddress(toSave.address, toSave.suburb, toSave.state, toSave.postcode ?? '')
        .then(coords => {
          if (!coords) return;
          const withCoords: PropertyRecord = { ...draftRef.current, latitude: coords.latitude, longitude: coords.longitude };
          setDraft(withCoords);
          onSave(withCoords);
        })
        .catch(() => {}); // geocoding is best-effort, never block
    }

    // Fire-and-forget HTAG address_key backfill — triggers edge function to populate
    // address_key for any properties that don't have one yet (needed for AVM estimates).
    // Function scopes to auth.uid() automatically; no body needed. SDK auto-attaches
    // the session JWT via Authorization header (B9 F6, Day 50 — was raw fetch with
    // anon key, which now 401s after the auth gate was added).
    if (!toSave.addressKey && toSave.address && toSave.suburb && toSave.state) {
      if (supabase) {
        supabase.functions.invoke("backfill-address-keys", { body: {} }).catch(() => {});
      }
    }
  }

  // Derived stats (used by both hero and compact bar)
  const effectiveMarket = draft.marketValue ?? draft.bankValuation ?? draft.currentValue;
  // B27-followup-3: usable equity (bank-lendable) and gross equity (raw owner
  // position) both route through totalLoanBalance(draft) so split + equity
  // releases are included. grossEquity is allowed to go negative (underwater).
  const totalDebt = totalLoanBalance(draft);
  const usableEq = Math.max(0, effectiveMarket * 0.8 - totalDebt);
  const grossEquity = effectiveMarket - totalDebt;
  const totalIncomeAnnual = (draft.incomeRows ?? []).reduce(
    (s, r) => s + toAnnual(r.amount, r.frequency as RowFreq), 0
  );
  // Exclude mortgage repayments — interest is part of the loan, not opex.
  // See calculations.ts EXCLUDED_EXPENSE_CATEGORIES.
  const totalExpenseAnnual = filterDeductibleExpenseRows(draft.expenseRows ?? []).reduce(
    (s, r) => s + toAnnual(r.amount, r.frequency as RowFreq), 0
  );
  // propertyMetrics().netCashflow is MONTHLY (gross, pre-share). Derive
  // annual/weekly/fortnightly here so display labels match the divisor used.
  // See ARCHITECTURE §7 — per-property displays are GROSS, not share-adjusted.
  const netCashflowMonthly = propertyMetrics(draft).netCashflow;
  const netCashflowAnnual = netCashflowMonthly * 12;
  const netCashflowWeekly = netCashflowAnnual / 52;
  const netCashflowFn = netCashflowAnnual / 26;
  // B27 #11: include split + equity-release loans (totalLoanBalance), not just
  // the primary balance. effectiveMarket stays as the denominator (renovation-
  // adjusted value); propertyMetrics().lvr uses raw currentValue, so we keep
  // the local computation rather than reading from the metrics object.
  const lvrPct = lvr(totalLoanBalance(draft), effectiveMarket);
  // B27-followup-5: loan breakdown for the compact-bar "Loan balance" pill
  // subtext. fmtCompact ($Xk/$X.XM) keeps the narrow pill subtext readable.
  // The AI prompt inside generatePropertySummary computes its own breakdown
  // with fmt() for full-figure clarity — different consumer, different format.
  const splitPartCompact = draft.splitLoan ? ` + ${fmtCompact(draft.splitLoanBalance ?? 0)} split` : "";
  const releasePillTotal = (draft.equityReleases ?? []).reduce((s, r) => s + (r.balance ?? r.amount ?? 0), 0);
  const releasePartCompact = releasePillTotal > 0 ? ` + ${fmtCompact(releasePillTotal)} release` : "";
  const loanBreakdownSub = (splitPartCompact || releasePartCompact)
    ? `${fmtCompact(draft.loanBalance ?? 0)} primary${splitPartCompact}${releasePartCompact}`
    : undefined;
  const entityStructure = structures.find((s) => s.id === draft.structureId);
  const structureName   = entityStructure?.displayName ?? draft.structureId;
  const entityType      = entityStructure?.entityType ?? null; // null = unassigned
  // B106 — personal-name = no structure assigned. structureId IS NULL is the SSOT.
  const isPersonalEntity = isPersonalProperty(draft);

  async function generatePropertySummary() {
    if (!isSupabaseEnabled()) return;
    setAiLoading(true);
    setAiError(null);
    setAiSummary("");
    // B27-followup-3: Loan balance reflects total (primary + split + releases);
    // when multiple components exist, an extra "Loan breakdown" line itemises
    // them so the model has the full picture rather than just the primary.
    const splitPart = draft.splitLoan ? ` + ${fmt(draft.splitLoanBalance ?? 0)} split` : "";
    const releaseTotal = (draft.equityReleases ?? []).reduce((s, r) => s + (r.balance ?? r.amount ?? 0), 0);
    const releasePart = releaseTotal > 0 ? ` + ${fmt(releaseTotal)} release` : "";
    const hasLoanBreakdown = !!splitPart || !!releasePart;
    const systemPrompt = [
      `Property: ${draft.nickname || draft.address}`,
      `Address: ${draft.address}`,
      `Asset class: ${draft.assetClass ?? "—"}`,
      `Structure: ${structureName ?? "Personal"}`,
      `Current value: ${fmt(effectiveMarket)}`,
      `Loan balance: ${fmt(totalDebt)}`,
      ...(hasLoanBreakdown
        ? [`Loan breakdown: ${fmt(draft.loanBalance ?? 0)} primary${splitPart}${releasePart} = ${fmt(totalDebt)} total`]
        : []),
      `LVR: ${lvrPct.toFixed(1)}%`,
      `Usable equity (@ 80% LVR): ${fmt(usableEq)}`,
      `Annual rental income: ${fmt(totalIncomeAnnual)}`,
      `Annual expenses (opex): ${fmt(totalExpenseAnnual)}`,
      `Annual loan interest (tax-deductible): ${fmt(annualLoanInterest(draft))}`,
      `Annual loan repayment (cashflow impact): ${fmt(annualLoanRepayment(draft).total)}`,
      `Net annual cashflow: ${fmt(netCashflowAnnual)}`,
    ].join("\n");
    const result = await callAi({
      systemPrompt,
      userPrompt: "Write a 2-paragraph plain English summary of this property's financial performance and strategic position within the portfolio. Be specific to the numbers. No generic advice.",
      maxTokens: 600,
    });
    if (result.ok) {
      setAiSummary((result.data as string).trim());
    } else {
      setAiError(result.error);
    }
    setAiLoading(false);
  }

  const propertyNotices = computePropertyNotices(draft, loadAppSettings());
  const activeAlerts = propertyNotices.filter((a) => {
    const dismissed = dismissedAlerts[a.id];
    return !dismissed || dismissed < Date.now();
  });

  // Action buttons shared between hero and compact bar
  function ActionButtons({ compact = false }: { compact?: boolean }) {
    return (
      <div className="flex gap-2">
        {!compact && onExportToSell && (
          <button
            onClick={() => onExportToSell(property.id)}
            className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
          >
            Export to Sell/Profit
          </button>
        )}
        {/* B130 Wave 2A close-out audit: original gate `!draft.status`
            only matched legacy rows where the column was null/undefined,
            so all 7 active properties (status === "active") could not
            open Mark As Sold on prod. Correct gate is "anything that's
            not already sold" — symmetric with the Sold pill below. */}
        {!compact && draft.status !== "sold" && (
          <button
            onClick={() => setMarkSoldModal(true)}
            className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
          >
            Mark as sold
          </button>
        )}
        {!compact && draft.status === "sold" && (
          <span className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-600 bg-red-50">
            Sold
          </span>
        )}
        {!compact && onDeleteProperty && (
          <button
            onClick={() => setDeleteModal(true)}
            className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            Delete property
          </button>
        )}
        <button
          onClick={() => setShowDocs(true)}
          className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
        >
          Documents
        </button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={pageRef} className="relative">
      <style>{`
        .compact-bar {
          opacity: 0;
          transform: translateY(-100%);
          pointer-events: none;
          transition: opacity 150ms ease, transform 150ms ease;
        }
        .hero-hidden .compact-bar {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
      `}</style>

      {/* ── Compact sticky bar (CSS-toggled when hero scrolls out) ──── */}
      <div className="compact-bar sticky top-0 z-20 -mx-8 -mt-0 border-b border-neutral-100 bg-white px-8 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-base font-semibold text-neutral-900">{draft.nickname}</span>
          <div className="flex flex-wrap items-center gap-2">
            <StatPill
              label="Value"
              value={fmt(effectiveMarket)}
              sub={(draft.ownershipPercent ?? 100) < 100 ? `your ${draft.ownershipPercent}% share: ${fmt(effectiveMarket * ((draft.ownershipPercent ?? 100) / 100))}` : undefined}
            />
            <StatPill
              label="Loan balance"
              value={fmt(totalDebt)}
              sub={loanBreakdownSub}
              info="Total of primary loan + split loan + equity releases."
            />
            <StatPill
              label="Equity"
              value={fmt(grossEquity)}
              info="Market value minus total loan balance (primary + split + equity releases). Negative means loans exceed value."
            />
            <StatPill
              label="Cashflow/wk"
              value={(netCashflowWeekly >= 0 ? "+" : "") + fmt(netCashflowWeekly)}
              sub={(draft.ownershipPercent ?? 100) < 100 ? `your share: ${(netCashflowWeekly * ((draft.ownershipPercent ?? 100) / 100) >= 0 ? "+" : "")}${fmt(netCashflowWeekly * ((draft.ownershipPercent ?? 100) / 100))}` : undefined}
            />
            <StatPill label="LVR" value={pct(lvrPct)} />
            <ActionButtons compact />
          </div>
        </div>
      </div>

      {/* ── Back + action buttons ────────────────────────────────────────── */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1 rounded-xl border border-neutral-200 px-3 py-1.5 text-sm text-neutral-500 hover:bg-neutral-50"
        >
          ← Back
        </button>
        <div className="flex items-center gap-2">
          {savedToast && (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              Saved
            </span>
          )}
          {saveError && <span className="text-xs text-red-500">{saveError}</span>}
          <ActionButtons />
        </div>
      </div>

      {/* ── Property Intelligence ─────────────────────────────────────────── */}
      {subscriptionTier === "investor" ? (
        <CollapsiblePanel
          title="Market Intelligence"
          subtitle={draft.lgaId ? `${draft.lgaName ?? draft.suburb ?? ""} · ${htagPropertyTypeLabel}` : undefined}
          rightSlot={
            <a href="https://developer.htagai.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-400 hover:text-neutral-600">
              Powered by <span className="font-medium text-[#1A6BF3]">HtAG Analytics</span>
            </a>
          }
          open={htagOpen}
          onOpenChange={setHtagOpen}
        >
          <SuburbIntelligenceSection
            property={draft}
            onUpdate={(updated) => { setDraft(updated); saveProperty(updated); }}
            htagData={htagData}
            htagLoading={htagLoading}
            htagError={htagError}
          />
        </CollapsiblePanel>
      ) : (
        <IntelligencePaywall onUpgrade={onNavigateToBilling} />
      )}

      {/* ── Property Details divider ──────────────────────────────────────── */}
      <div className="my-6 flex items-center gap-4">
        <div className="flex-1 h-px bg-neutral-200" />
        <span className="text-xs font-medium uppercase tracking-widest text-neutral-400">Property Details</span>
        <div className="flex-1 h-px bg-neutral-200" />
      </div>

      {/* ── Property name + identity + AI summary ───────────────────────── */}
      <div ref={heroRef} className="mb-6 rounded-2xl border border-neutral-100 bg-white p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <EF label="Nickname" value={draft.nickname ?? ""}
            onChange={(v) => setDraft((p) => ({ ...p, nickname: v }))}
            onBlur={saveProperty} />
          {/* B130 Wave 2A Phase 6 — explicit contract date input. Contract
              date is the CGT event date per ATO RPG 2025 p62; settlement
              (draft.purchaseDate) is retained for cashflow and ownership-
              period calculations. Phase 1 backfilled contract_date :=
              purchase_date for legacy rows; the amber banner below this
              grid prompts the user to refine if they were equal. */}
          <EF label="Contract Date" value={draft.contractDate ?? ""} type="date"
            tooltip="The date the purchase contract was signed. This is the CGT event date per ATO RPG 2025 p62 — used for the 12-month discount eligibility check and FY attribution. Often differs from settlement (which is when funds change hands)."
            onChange={(v) => setDraft((p) => ({ ...p, contractDate: v }))}
            onBlur={saveProperty} />
          <EF label="Settlement Date" value={draft.purchaseDate ?? ""} type="date"
            tooltip="The date settlement occurred and you took legal ownership. Used for cashflow modelling and ownership-period calculations. The CGT event date is the contract date, not settlement (per ATO RPG 2025 p62)."
            onChange={(v) => setDraft((p) => ({ ...p, purchaseDate: v }))}
            onBlur={saveProperty} />
          {/* B130 Wave 2C Phase 11 — PPOR-converted-to-rental toggle.
              Triggers s40-27(3) exception — items acquired during the
              PPOR period (before 9 May 2017) remain Div 40 eligible
              after conversion. Compact checkbox; banner surfaces in
              the Tax → Depreciation tab when checked. */}
          <div className="col-span-2 sm:col-span-3">
            <label className="inline-flex items-start gap-2 text-xs text-neutral-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={draft.pporConvertedFromPpor === true}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, pporConvertedFromPpor: e.target.checked || undefined }));
                  saveProperty();
                }}
              />
              <span>
                <strong>This was my principal place of residence (PPOR) before 9 May 2017, then converted to a rental.</strong>
                <span className="ml-1 text-neutral-500">
                  Triggers ITAA s40-27(3) — assets acquired during the PPOR period remain Div 40 eligible. Items acquired AFTER 9 May 2017 still follow the standard second-hand rule.
                </span>
              </span>
            </label>
          </div>
          {/* J7 — Address block. Three modes:
              1. Locked (default when address exists): summary line + Edit
                 button. No autocomplete, no checkbox, no raw fields.
              2. Editing search: autocomplete + manual-entry checkbox.
                 Picking from the list locks it back to summary view.
              3. Editing manual: 4 fields visible (Google can't find it).
                 Done button locks it back to summary view. */}
          <div className="col-span-2 sm:col-span-3">
            {/* Mode 1: Locked summary view */}
            {hasAddress && !editingAddress && (
              <>
                <label className="mb-0.5 block text-[11px] uppercase tracking-wider text-neutral-400">
                  Address
                </label>
                <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
                  <span>
                    {[draft.address, draft.suburb, draft.state, draft.postcode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAddress(true);
                      setManualAddress(false);
                    }}
                    className="shrink-0 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Edit
                  </button>
                </div>
              </>
            )}

            {/* Modes 2 & 3: Editing */}
            {editingAddress && !manualAddress && (
              <>
                <label className="mb-0.5 block text-[11px] uppercase tracking-wider text-neutral-400">
                  Address search
                </label>
                <AddressAutocomplete
                  initialValue=""
                  placeholder="Start typing an Australian address…"
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onSelect={(parsed) => {
                    const updated = {
                      ...draftRef.current,
                      address: parsed.address,
                      suburb: parsed.suburb,
                      state: parsed.state,
                      postcode: parsed.postcode,
                      latitude: parsed.latitude,
                      longitude: parsed.longitude,
                    };
                    setDraft(updated);
                    saveProperty(updated);
                    // Lock back to summary after a successful pick.
                    setEditingAddress(false);
                  }}
                />
              </>
            )}

            {/* Manual entry mode — 4 fields */}
            {editingAddress && manualAddress && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <EF label="Address" value={draft.address ?? ""}
                  onChange={(v) => setDraft((p) => ({ ...p, address: v }))}
                  onBlur={saveProperty} />
                <EF label="Suburb" value={draft.suburb ?? ""}
                  onChange={(v) => setDraft((p) => ({ ...p, suburb: v }))}
                  onBlur={saveProperty} />
                <EF label="State" value={draft.state ?? ""}
                  onChange={(v) => setDraft((p) => ({ ...p, state: v }))}
                  onBlur={saveProperty} />
                <EF label="Postcode" value={draft.postcode ?? ""}
                  onChange={(v) => setDraft((p) => ({ ...p, postcode: v }))}
                  onBlur={saveProperty} />
              </div>
            )}

            {/* Editing-mode controls: manual checkbox + Done button */}
            {editingAddress && (
              <div className="mt-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={manualAddress}
                    onChange={(e) => setManualAddress(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-neutral-300 text-blue-600 focus:ring-blue-400"
                  />
                  Enter address manually (Google can't find it)
                </label>
                {hasAddress && (
                  <button
                    type="button"
                    onClick={() => setEditingAddress(false)}
                    className="text-xs text-neutral-500 hover:text-neutral-700"
                  >
                    Done
                  </button>
                )}
              </div>
            )}
          </div>
          <EF label="Asset Class" value={draft.assetClass ?? ""} options={ASSET_CLASSES}
            onChange={(v) => setDraft((p) => ({ ...p, assetClass: v as PropertyAssetClass }))}
            onBlur={saveProperty} />
          <EF label="Property Type" value={draft.propertyType ?? ""} options={PROPERTY_STYLES}
            onChange={(v) => setDraft((p) => ({ ...p, propertyType: v as PropertyStyle }))}
            onBlur={saveProperty} />
          <div>
            <label className="mb-0.5 block text-[11px] uppercase tracking-wider text-neutral-400">Owning Structure</label>
            <select
              className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={draft.structureId ?? ""}
              onChange={(e) => setDraft((p) => ({ ...p, structureId: e.target.value || "" }))}
              onBlur={() => saveProperty()}
            >
              <option value="">Personal</option>
              {structures.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName || s.trustName || s.companyName || s.entityType}
                </option>
              ))}
            </select>
          </div>
          {isPersonalEntity && (() => {
            // B103 Phase 3 — Ownership editor (Sole / JT / TIC + co-owner picker).
            // Replaces the legacy single ownershipPercent input. ownershipPercent
            // is now a derived field maintained on every coOwners change for
            // back-compat with the 17 pre-Phase-4 read sites.
            const eligibleMembers = (profile?.householdMembers ?? [])
              .filter((m) => m.memberType !== "dependent");
            const hasEligibleMembers = eligibleMembers.length > 0;
            const mode = draft.ownershipMode;
            const coOwners = draft.coOwners ?? [];
            const totalCoSharePct = coOwners.reduce((s, c) => s + (c.sharePercent ?? 0), 0);
            const primaryShare = Math.max(0, 100 - totalCoSharePct);

            function applyModeChange(newMode: OwnershipMode | undefined) {
              // Confirm before resetting Sole when there are existing co-owners
              // (otherwise the user can lose their setup with one wrong click).
              if (newMode === "sole" && coOwners.length > 0) {
                if (!window.confirm("Switching to Sole will remove all co-owners and reset your share to 100%. Continue?")) {
                  return;
                }
              }
              const derived = deriveCoOwnersForMode(mode, coOwners, newMode, draft.ownershipPercent ?? 100);
              setDraft((p) => ({
                ...p,
                ownershipMode: newMode,
                coOwners: derived.coOwners,
                ownershipPercent: derived.ownershipPercent,
              }));
              saveProperty();
            }

            function updateCoOwnerMember(idx: number, memberId: string) {
              setDraft((p) => ({
                ...p,
                coOwners: (p.coOwners ?? []).map((c, i) => i === idx ? { ...c, householdMemberId: memberId } : c),
              }));
              saveProperty();
            }

            function updateCoOwnerShare(idx: number, sharePct: number) {
              setDraft((p) => {
                const newCos = (p.coOwners ?? []).map((c, i) => i === idx ? { ...c, sharePercent: sharePct } : c);
                const total = newCos.reduce((s, c) => s + (c.sharePercent ?? 0), 0);
                return { ...p, coOwners: newCos, ownershipPercent: Math.max(0, 100 - total) };
              });
              saveProperty();
            }

            function removeCoOwner(idx: number) {
              setDraft((p) => {
                const newCos = (p.coOwners ?? []).filter((_, i) => i !== idx);
                if (mode === "joint_tenants") {
                  // Re-equalise JT shares.
                  const totalHolders = newCos.length + 1;
                  const equalPct = Math.round(100 / totalHolders);
                  return {
                    ...p,
                    coOwners: newCos.map((c) => ({ ...c, sharePercent: equalPct })),
                    ownershipPercent: equalPct,
                  };
                }
                const total = newCos.reduce((s, c) => s + (c.sharePercent ?? 0), 0);
                return { ...p, coOwners: newCos, ownershipPercent: Math.max(0, 100 - total) };
              });
              saveProperty();
            }

            function addCoOwner() {
              const firstAvailable = eligibleMembers.find(
                (m) => !coOwners.some((c) => c.householdMemberId === m.id)
              );
              if (!firstAvailable) return;
              setDraft((p) => {
                const cos = p.coOwners ?? [];
                if (mode === "joint_tenants") {
                  const totalHolders = cos.length + 2; // primary + existing + new
                  const equalPct = Math.round(100 / totalHolders);
                  const newCos = [
                    ...cos.map((c) => ({ ...c, sharePercent: equalPct })),
                    { householdMemberId: firstAvailable.id, sharePercent: equalPct },
                  ];
                  return { ...p, coOwners: newCos, ownershipPercent: equalPct };
                }
                // TIC — new co-owner gets 0% by default; user must set
                const newCos = [...cos, { householdMemberId: firstAvailable.id, sharePercent: 0 }];
                const total = newCos.reduce((s, c) => s + (c.sharePercent ?? 0), 0);
                return { ...p, coOwners: newCos, ownershipPercent: Math.max(0, 100 - total) };
              });
              saveProperty();
            }

            const hasUnclaimedShare = mode === undefined && (draft.ownershipPercent ?? 100) < 100;

            return (
              <div className="col-span-2 sm:col-span-3">
                <div className="mb-1 flex items-center gap-1">
                  <label className="block text-xs text-neutral-500">Ownership</label>
                  <InfoTooltip tip="How is this property held legally? Sole = you alone. Joint Tenants (JT) = equal shares with right of survivorship — if one owner dies, their share automatically goes to the other(s). Tenants in Common (TIC) = explicit percentage shares; on death, the share passes via will. The ATO uses these shares to allocate rental income, expenses, and CGT — getting it wrong invites a tax-return audit." />
                </div>

                {/* Empty state — no eligible household members */}
                {!hasEligibleMembers && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    To set up co-ownership, first add a partner in your{" "}
                    {onNavigateToProfile ? (
                      <button onClick={onNavigateToProfile} className="font-medium underline hover:text-amber-900">Profile</button>
                    ) : (
                      <span className="font-medium">Profile</span>
                    )}
                    . Until then, this property is treated as solely owned by you.
                  </div>
                )}

                {hasEligibleMembers && (
                  <>
                    {/* Mode dropdown */}
                    <select
                      value={mode ?? ""}
                      onChange={(e) => applyModeChange((e.target.value || undefined) as OwnershipMode | undefined)}
                      className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">— Confirm ownership mode —</option>
                      <option value="sole">Sole (just me)</option>
                      <option value="joint_tenants">Joint Tenants (equal shares with right of survivorship)</option>
                      <option value="tenants_in_common">Tenants in Common (explicit percentage shares)</option>
                    </select>

                    {/* Legacy hint when mode unset and there's a missing share */}
                    {hasUnclaimedShare && (
                      <p className="mt-1 text-xs text-neutral-500">
                        Currently set to {draft.ownershipPercent}% share. Confirm ownership mode to enable household-scope features.
                      </p>
                    )}

                    {/* TIC ATO trap */}
                    {mode === "tenants_in_common" && (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        <strong>ATO trap:</strong> Tenants-in-Common income and expenses must be split <em>per the ownership percentage on the title</em>, not per who actually pays the loan or receives the rent. The shares below drive how AusProp allocates rental income, deductions, and CGT for each adult's tax return — make sure they match your title deed.
                      </div>
                    )}

                    {/* JT note */}
                    {mode === "joint_tenants" && (
                      <p className="mt-2 text-xs text-neutral-500">
                        Joint Tenants implies equal shares. ATO assumes 50/50 for 2 owners, ~33/33/33 for 3, etc. Add the other holder(s) below.
                      </p>
                    )}

                    {/* Co-owner editor */}
                    {(mode === "joint_tenants" || mode === "tenants_in_common") && (
                      <div className="mt-2 space-y-1.5">
                        {/* Primary's row — read-only */}
                        <div className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                          <span className="flex-1 text-neutral-700">
                            {profile?.preferredName || profile?.fullLegalName || "You"}{" "}
                            <span className="text-xs text-neutral-400">(primary)</span>
                          </span>
                          <span className="w-16 text-right text-sm font-medium text-neutral-700">{primaryShare}%</span>
                        </div>
                        {/* Co-owner rows */}
                        {coOwners.map((co, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <select
                              value={co.householdMemberId}
                              onChange={(e) => updateCoOwnerMember(idx, e.target.value)}
                              className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            >
                              <option value="">— select household member —</option>
                              {eligibleMembers
                                .filter((m) => m.id === co.householdMemberId || !coOwners.some((c) => c.householdMemberId === m.id))
                                .map((m) => (
                                  <option key={m.id} value={m.id}>{m.preferredName || m.fullLegalName}</option>
                                ))}
                            </select>
                            {mode === "tenants_in_common" ? (
                              <div className="flex items-center gap-1">
                                <NumberInput
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={co.sharePercent}
                                  onChange={(e) => updateCoOwnerShare(idx, parseFloat(e.target.value) || 0)}
                                  onBlur={() => saveProperty()}
                                  className="w-16 rounded-lg border border-neutral-200 px-2 py-1.5 text-right text-sm"
                                />
                                <span className="text-xs text-neutral-400">%</span>
                              </div>
                            ) : (
                              <span className="w-16 text-right text-sm font-medium text-neutral-700">{co.sharePercent}%</span>
                            )}
                            <button
                              onClick={() => removeCoOwner(idx)}
                              className="px-1 text-neutral-300 hover:text-red-500"
                              aria-label="Remove co-owner"
                            >×</button>
                          </div>
                        ))}
                        {/* Add co-owner button (hidden when all eligible members are already added) */}
                        {eligibleMembers.some((m) => !coOwners.some((c) => c.householdMemberId === m.id)) && (
                          <button
                            onClick={addCoOwner}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            + Add co-owner
                          </button>
                        )}
                        {/* TIC validation: total > 100 */}
                        {mode === "tenants_in_common" && totalCoSharePct > 100 && (
                          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            Co-owner shares total {totalCoSharePct}% — must be ≤ 100%. Adjust the figures so they match your title deed.
                          </div>
                        )}
                      </div>
                    )}

                    <p className="mt-2 text-xs text-neutral-400">
                      For trust / company / SMSF properties, ownership is handled via your structure (Profile → Structures).
                    </p>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* B130 Wave 2A Phase 6 — legacy contract-date prompt. Shown when
            contract date equals settlement date (Phase 1 backfill heuristic)
            so the user can refine. Per-property dismiss via localStorage. */}
        {(() => {
          const showContractPrompt =
            !contractPromptDismissed
            && !!draft.contractDate
            && !!draft.purchaseDate
            && draft.contractDate === draft.purchaseDate;
          if (!showContractPrompt) return null;
          return (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong>Contract date assumed.</strong> We've set your contract date to match your settlement date. If your purchase contract was actually signed on a different date, update the Contract Date above. The contract date is what the ATO uses for CGT timing — getting it right matters for the 12-month discount and FY attribution.
                </div>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.setItem(`contractDatePrompt:dismissed:${property.id}`, "1");
                    } catch { /* ignore */ }
                    setContractPromptDismissed(true);
                  }}
                  className="shrink-0 rounded-lg border border-amber-300 px-2.5 py-1 text-xs text-amber-700 hover:bg-amber-100"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })()}

        {/* AI Summary */}
        <div className="mt-4 border-t border-neutral-100 pt-4">
          {isSupabaseEnabled() && (
            <button
              type="button"
              onClick={() => void generatePropertySummary()}
              disabled={aiLoading}
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-700 disabled:opacity-60 transition-colors"
            >
              ✦ {aiLoading ? "Generating..." : "AI Summary"}
            </button>
          )}
          {aiError && (
            <div className="mt-2">
              <p className="text-xs text-red-600">{aiErrorMessage(aiError)}</p>
              {aiError.kind !== "parse" && (
                <button type="button" onClick={() => void generatePropertySummary()} className="mt-1 text-xs text-neutral-500 underline hover:text-neutral-700">Retry</button>
              )}
            </div>
          )}
          {aiSummary && (
            <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 space-y-2">
              {aiSummary.split(/\n+/).filter(Boolean).map((para, i) => (
                <p key={i} className="text-sm text-neutral-700 leading-relaxed">
                  {para.split(/\*\*(.+?)\*\*/g).map((part, j) =>
                    j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                  )}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Two-column body ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* 1. VALUATIONS — Change 3: only market/bank value + equity panel */}
          {(() => {
            const mv = (draft.marketValue ?? draft.currentValue);
            const bv = (draft.bankValuation ?? draft.currentValue);
            // B27-followup-3: usable equity at 80% LVR uses totalLoanBalance
            // so split + equity-release loans are deducted, not just primary.
            const eqMarket = Math.max(0, mv * 0.8 - totalLoanBalance(draft));
            const eqBank = Math.max(0, bv * 0.8 - totalLoanBalance(draft));
            return (
              <CollapsibleSection
                id="valuations"
                collapsed={!!collapsed["valuations"]}
                onToggle={() => toggleCollapse("valuations")}
                title="Valuations"
                summary={
                  draft.unimprovedLandValue
                    ? `Market ${fmt(mv)} · Bank ${fmt(bv)} · ULV ${fmt(draft.unimprovedLandValue)} · Equity ${fmt(eqMarket)}`
                    : `Market ${fmt(mv)} · Bank ${fmt(bv)} · Equity ${fmt(eqMarket)}`
                }
              >
                <div className="grid grid-cols-3 gap-3">
                  <EF label="Market Value ($)" value={draft.marketValue ?? ""}
                    type="number"
                    onChange={(v) => setDraft((p) => ({ ...p, marketValue: Number(v) || 0, currentValue: Number(v) || 0 }))}
                    onBlur={saveProperty} />
                  <EF label="Bank Valuation ($)" value={draft.bankValuation ?? ""}
                    type="number"
                    onChange={(v) => setDraft((p) => ({ ...p, bankValuation: Number(v) || 0 }))}
                    onBlur={saveProperty} />
                  <EF label="Valuation Date" value={draft.lastValuationDate ?? ""} type="date"
                    onChange={(v) => setDraft((p) => ({ ...p, lastValuationDate: v }))}
                    onBlur={saveProperty} />
                  <div>
                    <label className="mb-1 flex items-center text-[11px] uppercase tracking-wider text-neutral-400">
                      Unimproved Land Value (ULV)
                      <InfoTooltip tip="Each state levies land tax on the unimproved land value — the value of the land only, excluding buildings and improvements. This figure appears on your council rates notice and is typically 40–70% of market value for houses, and lower for units (where land is shared across all owners). Entering the correct ULV makes your Land Tax tab estimates significantly more accurate." />
                    </label>
                    <NumberInput
                      className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={draft.unimprovedLandValue ?? ""}
                      placeholder="Leave blank to use market value as proxy"
                      onChange={(e) => {
                        const raw = e.target.value;
                        setDraft((p) => ({
                          ...p,
                          unimprovedLandValue: raw === "" ? null : Number(raw) || 0,
                        }));
                      }}
                      onBlur={saveProperty}
                    />
                  </div>
                </div>
                <p className="mt-1.5 text-[11px] text-neutral-500 leading-relaxed">
                  <strong>ULV</strong> — the land-only value used by your state to calculate land tax.
                  Find it on your annual council rates notice under "Site Value", "Unimproved Value", or
                  "Land Value". Leave blank to use market value as a proxy in the Land Tax tab (overestimates).
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl bg-neutral-50 p-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-neutral-400">Equity @ 80% Market</div>
                    <div className="mt-1 text-base font-semibold text-neutral-900">{fmt(eqMarket)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-neutral-400">Equity @ 80% Bank</div>
                    <div className="mt-1 text-base font-semibold text-neutral-900">{fmt(eqBank)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-neutral-400">Difference</div>
                    <div className={`mt-1 text-base font-semibold ${eqMarket >= eqBank ? "text-emerald-700" : "text-red-600"}`}>
                      {eqMarket >= eqBank ? "+" : ""}{fmt(eqMarket - eqBank)}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            );
          })()}

          {/* 2. ACQUISITION COSTS — Change 4: new section */}
          {(() => {
            const acqRows = draft.acquisitionCosts ?? [];
            const totalAcq = acqRows.reduce((s, r) => s + r.amount, 0);
            const totalCost = draft.purchasePrice + totalAcq;

            return (
              <CollapsibleSection
                id="acquisition"
                collapsed={!!collapsed["acquisition"]}
                onToggle={() => toggleCollapse("acquisition")}
                title="Acquisition Costs"
                summary={`Total acquisition ${fmt(totalCost)} · ${acqRows.length} cost items`}
              >
                {/* Why this matters — CGT cost base context */}
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <span>Why this matters</span>
                    <InfoTooltip tip="Acquisition costs are added to your purchase price to form your CGT cost base. They include stamp duty, legal fees, inspections, and any other costs you paid to buy the property. Do not include ongoing costs like loan interest or rates — those are deductible each year but do not go into the cost base." />
                  </div>
                  <p className="mt-1 text-xs leading-relaxed">
                    These costs form part of your CGT cost base — the figure used to calculate
                    your taxable profit when you sell. The more accurately you record these
                    now, the lower your tax bill could be at sale. They flow automatically into
                    the <strong>CGT &amp; Cost Base</strong> tab in the Tax section.
                  </p>
                </div>

                {/* Purchase price */}
                <div className="mb-4">
                  <EF label="Purchase Price ($)" value={draft.purchasePrice ?? ""} type="number"
                    onChange={(v) => setDraft((p) => ({ ...p, purchasePrice: Number(v) || 0 }))}
                    onBlur={saveProperty} />
                </div>

                <AcquisitionTable
                  rows={acqRows}
                  onChangeRow={(id, field, value) => {
                    const updated = { ...draftRef.current, acquisitionCosts: acqRows.map((r) =>
                      r.id === id ? { ...r, [field]: field === "amount" ? Number(value) : value } : r
                    )};
                    setDraft(updated);
                  }}
                  onDeleteRow={(id) => {
                    const updated = { ...draftRef.current, acquisitionCosts: acqRows.filter((r) => r.id !== id) };
                    setDraft(updated);
                    saveProperty(updated);
                  }}
                  onAddRow={() => {
                    const updated = { ...draftRef.current, acquisitionCosts: [...acqRows, { id: uid(), category: "", amount: 0 }] };
                    setDraft(updated);
                    saveProperty(updated);
                  }}
                  onSave={saveProperty}
                />

                {/* Total cost to acquire */}
                <div className="mt-3 flex items-center justify-between rounded-xl bg-neutral-900 px-4 py-3 text-sm text-white">
                  <span className="font-medium">Total cost to acquire</span>
                  <span className="font-semibold">{fmt(totalCost)}</span>
                </div>
              </CollapsibleSection>
            );
          })()}


        </div>

        {/* ── RIGHT COLUMN ────────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* 6. TENANCY */}
          {(() => {
            const leaseEndDays = draft.leaseEndDate ? daysUntil(draft.leaseEndDate) : null;
            const leaseBadgeColor =
              leaseEndDays === null ? "" :
              leaseEndDays > 90 ? "bg-emerald-100 text-emerald-800" :
              leaseEndDays > 30 ? "bg-amber-100 text-amber-800" :
              "bg-red-100 text-red-700";

            async function handleSectionUpload(file: File, tag: "contract" | "lease") {
              if (!userId) {
                setSectionUploadError("Sign in required to upload documents.");
                return;
              }
              const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/heic", "image/heif"];
              const MAX_SIZE = 10 * 1024 * 1024;
              if (!ACCEPTED_MIME.includes(file.type)) {
                setSectionUploadError(`Only PDF/JPG/PNG/HEIC accepted (got ${file.type || "unknown"}).`);
                return;
              }
              if (file.size > MAX_SIZE) {
                setSectionUploadError("File size must be under 10 MB.");
                return;
              }
              setSectionUploadError(null);
              setSectionUploading(tag);
              const result = await uploadToStorage("property-documents", `${userId}/${draft.id}/${tag}`, file);
              setSectionUploading(null);
              if (!result.ok) {
                setSectionUploadError(result.error);
                return;
              }
              const updated: PropertyRecord = {
                ...draft,
                documents: [
                  ...(draft.documents ?? []),
                  {
                    id: uid(),
                    filename: file.name,
                    type: file.type,
                    dateUploaded: new Date().toISOString().slice(0, 10),
                    filePath: result.result.filePath,
                    fileSize: result.result.fileSize,
                    needsReupload: false,
                    tag,
                  },
                ],
              };
              setDraft(updated);
              onSave(updated);
            }

            return (
              <CollapsibleSection
                id="tenancy"
                collapsed={!!collapsed["tenancy"]}
                onToggle={() => toggleCollapse("tenancy")}
                title="Tenancy"
                summary={
                  draft.leaseEndDate
                    ? `Lease ends ${formatDate(draft.leaseEndDate)} · ${leaseEndDays} days · ${draft.weeklyRent ? fmt(draft.weeklyRent) + "/wk" : "—"}`
                    : `${draft.weeklyRent ? fmt(draft.weeklyRent) + "/wk" : "No lease data"}`
                }
              >
                {draft.leaseEndDate && leaseEndDays !== null && (
                  <div className={`mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${leaseBadgeColor}`}>
                    {leaseEndDays > 0
                      ? `${leaseEndDays} days until lease expiry`
                      : `Lease expired ${Math.abs(leaseEndDays)} days ago`}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <EF label="Lease Start" value={draft.leaseStartDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, leaseStartDate: v }))} onBlur={saveProperty} />
                  <EF label="Lease End" value={draft.leaseEndDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, leaseEndDate: v }))} onBlur={saveProperty} />
                  <EF label="Rent Review Date" value={draft.rentReviewDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, rentReviewDate: v }))} onBlur={saveProperty} />
                  <EF label="Weekly Rent ($)" value={draft.weeklyRent ?? ""} type="number" onChange={(v) => setDraft((p) => ({ ...p, weeklyRent: Number(v) || 0 }))} onBlur={saveProperty} />
                  <EF label="Bond Held ($)" value={draft.bondHeld ?? ""} type="number" onChange={(v) => setDraft((p) => ({ ...p, bondHeld: Number(v) || 0 }))} onBlur={saveProperty} />
                  <EF label="Tenant Name" value={draft.tenantName ?? ""} onChange={(v) => setDraft((p) => ({ ...p, tenantName: v }))} onBlur={saveProperty} />
                  <EF label="PM Name" value={draft.propertyManagerName ?? ""} onChange={(v) => setDraft((p) => ({ ...p, propertyManagerName: v }))} onBlur={saveProperty} />
                  <EF label="PM Email" value={draft.propertyManagerEmail ?? ""} type="email" onChange={(v) => setDraft((p) => ({ ...p, propertyManagerEmail: v }))} onBlur={saveProperty} />
                  <EF label="PM Phone" value={draft.propertyManagerPhone ?? ""} type="tel" onChange={(v) => setDraft((p) => ({ ...p, propertyManagerPhone: v }))} onBlur={saveProperty} />
                  <EF label="Mgmt Fee (%)" value={draft.managementFeePercent ?? ""} type="number" step="0.1" onChange={(v) => setDraft((p) => ({ ...p, managementFeePercent: Number(v) || 0 }))} onBlur={saveProperty} />
                  <EF label="Letting Fee (wks)" value={draft.lettingFeeWeeks ?? ""} type="number" onChange={(v) => setDraft((p) => ({ ...p, lettingFeeWeeks: Number(v) || 0 }))} onBlur={saveProperty} />
                  <EF label="Renewal Fee (wks)" value={draft.renewalFeeWeeks ?? ""} type="number" onChange={(v) => setDraft((p) => ({ ...p, renewalFeeWeeks: Number(v) || 0 }))} onBlur={saveProperty} />
                </div>

                {/* Compliance & Rates dates */}
                <div className="mt-4 border-t border-neutral-100 pt-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-3">Compliance & Rates</div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <EF label="Smoke Alarm Cert Expiry" value={draft.smokeAlarmExpiryDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, smokeAlarmExpiryDate: v }))} onBlur={saveProperty} />
                    <EF label="Pool Safety Cert Expiry" value={draft.poolSafetyCertDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, poolSafetyCertDate: v }))} onBlur={saveProperty} />
                    <EF label="Council Rates Due" value={draft.councilRatesDueDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, councilRatesDueDate: v }))} onBlur={saveProperty} />
                    <EF label="Water Rates Due" value={draft.waterRatesDueDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, waterRatesDueDate: v }))} onBlur={saveProperty} />
                    <EF label="Strata Levy Due" value={draft.strataLevyDueDate ?? ""} type="date" onChange={(v) => setDraft((p) => ({ ...p, strataLevyDueDate: v }))} onBlur={saveProperty} />
                  </div>
                </div>

                {/* Rent escalation sub-section — feeds the simulator's growth forecast */}
                <div className="mt-5 border-t border-neutral-100 pt-4">
                  <div className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-neutral-500">
                    Rent escalation
                    <InfoTooltip tip="Estimate how much your rent will increase each year. Used by the simulator's growth forecast to project future cashflow over 1, 5 and 10 years. Most residential leases include a fixed annual increase (typically 3–5%) or are tied to CPI. Choose Fixed and enter your rate, CPI to use the current ~2.8% Australian CPI assumption, or Market if you'll renegotiate at each renewal (the simulator will use its default 4% assumption). Leave as default if you're unsure — the simulator's per-year override on the Growth Forecast tab still lets you tune assumptions on the fly." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EF
                      label="Type"
                      value={draft.rentEscalationType ?? ""}
                      options={["", "CPI", "Fixed", "Market"]}
                      onChange={(v) => {
                        const next = (v || undefined) as "CPI" | "Fixed" | "Market" | undefined;
                        const updated = { ...draftRef.current, rentEscalationType: next };
                        setDraft(updated);
                        saveProperty(updated);
                      }}
                      onBlur={saveProperty}
                    />
                    <EF
                      label="Rate (%)"
                      value={draft.rentEscalationType === "Fixed" ? (draft.rentEscalationRate ?? "") : ""}
                      type="number"
                      step="0.1"
                      disabled={draft.rentEscalationType !== "Fixed"}
                      onChange={(v) => setDraft((p) => ({ ...p, rentEscalationRate: Number(v) || 0 }))}
                      onBlur={saveProperty}
                    />
                  </div>
                  {draft.rentEscalationType === "CPI" && (
                    <p className="mt-2 text-xs text-neutral-400">Using ~2.8% p.a. CPI assumption (constant — wired to live RBA data in a future release).</p>
                  )}
                  {draft.rentEscalationType === "Market" && (
                    <p className="mt-2 text-xs text-neutral-400">Market means you'll renegotiate at each renewal — the simulator will fall back to its default 4% growth assumption.</p>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => pmFileRef.current?.click()}
                    disabled={!userId || sectionUploading !== null}
                    className="rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-500 hover:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {sectionUploading === "contract" ? "Uploading…" : "⬆ PM Agreement"}
                  </button>
                  <input ref={pmFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" className="hidden"
                    onChange={(e) => { if (e.target.files?.[0]) void handleSectionUpload(e.target.files[0], "contract"); e.target.value = ""; }} />
                  <button
                    onClick={() => leaseFileRef.current?.click()}
                    disabled={!userId || sectionUploading !== null}
                    className="rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-500 hover:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {sectionUploading === "lease" ? "Uploading…" : "⬆ Lease Agreement"}
                  </button>
                  <input ref={leaseFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" className="hidden"
                    onChange={(e) => { if (e.target.files?.[0]) void handleSectionUpload(e.target.files[0], "lease"); e.target.value = ""; }} />
                </div>
                {sectionUploadError && (
                  <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{sectionUploadError}</div>
                )}
              </CollapsibleSection>
            );
          })()}

          {/* 7. INSURANCE POLICIES */}
          {(() => {
            const policies = draft.insurancePolicies ?? [];

            function updatePolicy(id: string, changes: Partial<InsurancePolicy>, andSave = false) {
              const updated = { ...draftRef.current, insurancePolicies: policies.map((x) => x.id === id ? { ...x, ...changes } : x) };
              setDraft(updated);
              if (andSave) saveProperty(updated);
            }

            function nextRenewal(): string {
              if (policies.length === 0) return "No policies";
              const upcoming = policies
                .filter((p) => p.endDate)
                .sort((a, b) => Date.parse(a.endDate) - Date.parse(b.endDate))[0];
              return upcoming ? `Next renewal ${formatDate(upcoming.endDate)}` : "—";
            }

            return (
              <CollapsibleSection
                id="insurance"
                collapsed={!!collapsed["insurance"]}
                onToggle={() => toggleCollapse("insurance")}
                title="Insurance Policies"
                summary={`${policies.length} ${policies.length === 1 ? "policy" : "policies"} · ${nextRenewal()}`}
              >
                <div className="space-y-3">
                  {policies.map((pol) => {
                    const days = pol.endDate ? daysUntil(pol.endDate) : null;
                    const nearExpiry = days !== null && days < 60;
                    return (
                      <div key={pol.id} className="rounded-xl border border-neutral-100 bg-neutral-50 p-3">
                        {nearExpiry && (
                          <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                            Renews in {days} days
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <EF label="Type" value={pol.type} options={INSURANCE_TYPES}
                            onChange={(v) => updatePolicy(pol.id, { type: v })} onBlur={saveProperty} />
                          <EF label="Insurer" value={pol.insurer}
                            onChange={(v) => updatePolicy(pol.id, { insurer: v })} onBlur={saveProperty} />
                          <EF label="Policy No." value={pol.policyNumber}
                            onChange={(v) => updatePolicy(pol.id, { policyNumber: v })} onBlur={saveProperty} />
                          <EF label="Premium ($)" value={pol.premium} type="number"
                            onChange={(v) => updatePolicy(pol.id, { premium: Number(v) || 0 })} onBlur={saveProperty} />
                          <EF label="Frequency" value={pol.frequency} options={["monthly", "yearly"]}
                            onChange={(v) => updatePolicy(pol.id, { frequency: v as "monthly" | "yearly" })} onBlur={saveProperty} />
                          <EF label="Start Date" value={pol.startDate} type="date"
                            onChange={(v) => updatePolicy(pol.id, { startDate: v })} onBlur={saveProperty} />
                          <EF label="End Date" value={pol.endDate} type="date"
                            onChange={(v) => updatePolicy(pol.id, { endDate: v })} onBlur={saveProperty} />
                          <div className="flex items-center gap-2 mt-1">
                            <input type="checkbox" checked={pol.reminderEnabled}
                              onChange={(e) => updatePolicy(pol.id, { reminderEnabled: e.target.checked }, true)} />
                            <span className="text-xs text-neutral-600">Renewal reminder</span>
                          </div>
                          <button
                            onClick={() => {
                              const updated = { ...draftRef.current, insurancePolicies: policies.filter((x) => x.id !== pol.id) };
                              setDraft(updated);
                              saveProperty(updated);
                            }}
                            className="col-span-2 mt-1 rounded-lg border border-red-100 bg-red-50 py-1 text-xs text-red-600 hover:bg-red-100"
                          >Remove policy</button>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => {
                      const updated = { ...draftRef.current, insurancePolicies: [...policies, { id: uid(), type: "Landlord", insurer: "", policyNumber: "", premium: 0, frequency: "yearly" as const, startDate: "", endDate: "", reminderEnabled: true }] };
                      setDraft(updated);
                      saveProperty(updated);
                    }}
                    className="w-full rounded-xl border border-dashed border-neutral-300 py-2 text-xs text-neutral-500 hover:border-neutral-400"
                  >
                    + Add policy
                  </button>
                </div>
              </CollapsibleSection>
            );
          })()}

          {/* 8. REMINDERS & ALERTS */}
          <CollapsibleSection
            id="alerts"
            title="Reminders & Alerts"
            summary={`${activeAlerts.length} active ${activeAlerts.length === 1 ? "reminder" : "reminders"}`}
            collapsed={!!collapsed["alerts"]}
            onToggle={() => toggleCollapse("alerts")}
          >
            {activeAlerts.length === 0 ? (
              <p className="text-sm text-neutral-400">No active reminders.</p>
            ) : (
              <div className="space-y-1.5">
                {activeAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-center gap-2">
                    <div className="flex-1"><NoticeItem item={alert} compact onEmail={setEmailNotice} /></div>
                    <button
                      onClick={() => setDismissedAlerts((p) => ({ ...p, [alert.id]: Date.now() + 7 * 86_400_000 }))}
                      className="shrink-0 rounded-lg border border-neutral-200 px-2.5 py-1 text-xs text-neutral-400 hover:text-neutral-600"
                    >
                      Dismiss 7d
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          {/* 9. VALUE & RENT HISTORY (N1) */}
          {(() => {
            const valueCount = (draft.valueHistory ?? []).length;
            const rentCount = (draft.rentHistory ?? []).length;
            const summary = valueCount + rentCount === 0
              ? "No history captured yet"
              : `${valueCount} value snapshot${valueCount === 1 ? "" : "s"} · ${rentCount} rent snapshot${rentCount === 1 ? "" : "s"}`;
            return (
              <CollapsibleSection
                id="history"
                collapsed={!!collapsed["history"]}
                onToggle={() => toggleCollapse("history")}
                title="Value & Rent History"
                summary={summary}
              >
                <PropertyHistoryTab property={draft} onUpdateProperty={onSave} />
              </CollapsibleSection>
            );
          })()}

          {/* 10. NOTES */}
          {(() => {
            const summary = draft.notes
              ? draft.notes.slice(0, 60) + (draft.notes.length > 60 ? "…" : "")
              : "No notes";
            return (
              <CollapsibleSection
                id="notes"
                collapsed={!!collapsed["notes"]}
                onToggle={() => toggleCollapse("notes")}
                title="Notes"
                summary={summary}
              >
                <textarea
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows={5}
                  value={draft.notes ?? ""}
                  onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                  onBlur={() => saveProperty()}
                />
              </CollapsibleSection>
            );
          })()}

          {/* 10. DOCUMENTS & RECEIPTS */}
          {(() => {
            const isOpen = !(collapsed["documents"] ?? true);
            return (
              <div
                className="rounded-2xl border border-neutral-100 bg-white overflow-hidden"
              >
                <div
                  className="flex items-center justify-between px-5 py-3.5 cursor-pointer select-none hover:bg-neutral-50"
                  onClick={() => setCollapsed((c) => ({ ...c, documents: !(c["documents"] ?? true) }))}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-neutral-400 text-xs">
                      {collapsed["documents"] ?? true ? "▶" : "▼"}
                    </span>
                    <span className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                      Documents &amp; Receipts
                    </span>
                    {(collapsed["documents"] ?? true) && (
                      <span className="ml-2 text-xs text-neutral-500">Leases, invoices, receipts, reports</span>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-neutral-100 px-5 py-4">
                    {userId ? (
                      <PropertyDocuments propertyId={property.id} userId={userId} />
                    ) : (
                      <p className="text-sm text-neutral-400">Sign in to upload and manage documents.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

        </div>
      </div>

      {/* ── EXPENSES & INCOME — full-width below the two-column grid ─────── */}
      <div className="mt-4 space-y-4">
        {/* 4. EXPENSES */}
        {(() => {
          const rows = draft.expenseRows ?? [];
          // Exclude mortgage repayment rows from totals — interest is part of
          // the loan record, not opex. See calculations.ts EXCLUDED_EXPENSE_CATEGORIES.
          const deductibleRows = filterDeductibleExpenseRows(rows);
          const opexAnnual = deductibleRows.reduce((s, r) => s + toAnnual(r.amount, r.frequency as RowFreq), 0);
          const interestAnnual = annualLoanInterest(draft);
          const totalWithInterest = opexAnnual + interestAnnual;
          // Day 37 guard — flag any mortgage repayment rows so users know
          // to remove them. The row exists in storage but is excluded from
          // every calculation.
          const mortgageRows = rows.filter((r) => isExcludedExpenseCategory(r.category) && (r.amount ?? 0) > 0);
          return (
            <CollapsibleSection
              id="expenses"
              collapsed={!!collapsed["expenses"]}
              onToggle={() => toggleCollapse("expenses")}
              title="Expenses"
              summary={`${deductibleRows.length} expenses${interestAnnual > 0 ? " + interest" : ""} · ${fmt(totalWithInterest)} / year · ${fmt(totalWithInterest / 26)} / fn`}
            >
              <p className="mb-3 text-xs text-neutral-400">
                Loan interest is shown below as a read-only row, calculated from your <strong className="text-neutral-500">Loans</strong> tab. Only enter property operating expenses here (insurance, rates, management fees, maintenance, etc.) — do not add loan repayments manually.
              </p>
              {mortgageRows.length > 0 && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  <strong>Mortgage repayments don't belong here.</strong> Loan interest is already tracked from your loan balance and rate (see the <strong>Loans</strong> tab). Including {mortgageRows.length === 1 ? "this row" : "these rows"} as an expense would double-count interest and incorrectly expense the principal component (which isn't deductible). The {mortgageRows.length === 1 ? "row is" : "rows are"} excluded from all calculations — please delete {mortgageRows.length === 1 ? "it" : "them"} below and enter loan details on the Loans tab instead.
                </div>
              )}
              {/* Synthetic read-only loan interest row — FIRST, before user-entered expenses */}
              {interestAnnual > 0 && (
                <div className="overflow-x-auto mb-1">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="bg-neutral-50">
                        <td className="py-1.5 pr-1 w-5" />
                        <td className="py-1.5 pr-3">
                          <span className="flex items-center gap-1.5 text-sm text-neutral-700">
                            Loan interest
                            <span className="text-xs text-neutral-400">(from Loans tab)</span>
                            <InfoTooltip tip="Interest only — calculated from your current loan balance × rate (including offset balance if set). This is the tax-deductible portion. Principal repayments are not shown because they are not an expense — they reduce your loan balance. Update via the Loans tab." />
                          </span>
                        </td>
                        <td className="py-1.5 pr-3 w-32" />
                        <td className="py-1.5 pr-3 w-24 text-xs text-neutral-400">Annual</td>
                        <td className="py-1.5 pr-3 w-32 text-right text-neutral-700">{fmt(interestAnnual)}</td>
                        <td className="py-1.5 pr-3 w-32 text-right text-neutral-500">{fmt(interestAnnual / 26)}</td>
                        <td className="py-1.5 w-6" />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <SpreadsheetTable
                rows={rows}
                onChangeRow={(id, field, value) => {
                  setDraft((p) => ({
                    ...p,
                    expenseRows: (p.expenseRows ?? []).map((r) =>
                      r.id === id ? { ...r, [field]: field === "amount" ? Number(value) : value } : r
                    ) as ExpenseRow[],
                  }));
                }}
                onDeleteRow={(id) => {
                  const updated = { ...draftRef.current, expenseRows: rows.filter((r) => r.id !== id) };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                onAddRow={() => {
                  const updated = { ...draftRef.current, expenseRows: [...rows, { id: uid(), category: "", amount: 0, frequency: "monthly" as RowFreq }] };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                addLabel="Add expense"
                onReorder={(newRows) => {
                  const updated = { ...draftRef.current, expenseRows: newRows as ExpenseRow[] };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                onSave={saveProperty}
              />
              {/* Combined total (opex + interest) */}
              {interestAnnual > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tfoot>
                      <tr className="border-t-2 border-neutral-200 font-semibold text-neutral-800">
                        <td className="pt-2 pr-3 w-5" />
                        <td className="pt-2 pr-3" colSpan={3}>Total (incl. interest)</td>
                        <td className="pt-2 pr-3 w-32 text-right">{fmt(totalWithInterest)}</td>
                        <td className="pt-2 pr-3 w-32 text-right">{fmt(totalWithInterest / 26)}</td>
                        <td className="w-6" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          );
        })()}

        {/* 5. INCOME */}
        {(() => {
          const rows = draft.incomeRows ?? [];
          const ownerPct = draft.ownershipPercent ?? 100;
          // Per-property displays are GROSS (ARCHITECTURE §7). Co-ownership share
          // is shown in the subtext, not multiplied into the headline figure.
          const displayNetAnnual = netCashflowAnnual;
          const displayNetFn = netCashflowFn;
          return (
            <CollapsibleSection
              id="income"
              collapsed={!!collapsed["income"]}
              onToggle={() => toggleCollapse("income")}
              title="Income"
              summary={`Net ${displayNetAnnual >= 0 ? "+" : ""}${fmt(displayNetAnnual)} / year · ${fmt(displayNetFn)} / fn${ownerPct < 100 ? ` · gross (your ${ownerPct}% share)` : ""}`}
            >
              <SpreadsheetTable
                rows={rows}
                onChangeRow={(id, field, value) => {
                  setDraft((p) => ({
                    ...p,
                    incomeRows: (p.incomeRows ?? []).map((r) =>
                      r.id === id ? { ...r, [field]: field === "amount" ? Number(value) : value } : r
                    ) as IncomeRow[],
                  }));
                }}
                onDeleteRow={(id) => {
                  const updated = { ...draftRef.current, incomeRows: rows.filter((r) => r.id !== id) };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                onAddRow={() => {
                  const updated = { ...draftRef.current, incomeRows: [...rows, { id: uid(), category: "", amount: 0, frequency: "weekly" as RowFreq }] };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                addLabel="Add income"
                onReorder={(newRows) => {
                  const updated = { ...draftRef.current, incomeRows: newRows as IncomeRow[] };
                  setDraft(updated);
                  saveProperty(updated);
                }}
                onSave={saveProperty}
              />
              <div className={`mt-3 flex items-center justify-between rounded-xl px-4 py-3 font-semibold ${
                displayNetAnnual >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
              }`}>
                <span className="text-sm">Net Cashflow{ownerPct < 100 ? ` · gross (your ${ownerPct}% share)` : ""}</span>
                <div className="flex gap-6 text-sm">
                  <span>{displayNetAnnual >= 0 ? "+" : ""}{fmt(displayNetAnnual)} / yr</span>
                  <span>{displayNetFn >= 0 ? "+" : ""}{fmt(displayNetFn)} / fn</span>
                </div>
              </div>
              {displayNetAnnual < 0 && (() => {
                const annualShortfall = Math.abs(displayNetAnnual);
                if (isPersonalEntity) {
                  // Effective marginal rate (bracket + Medicare + MLS + HELP) when
                  // profile is threaded; falls back to nominal investorMarginalRate
                  // for backwards-compat. See ARCHITECTURE §7 + L35.
                  const annualTaxSaving = profile
                    ? Math.round(personalTaxSavingFromLoss(annualShortfall, profile))
                    : Math.round(annualShortfall * (investorMarginalRate / 100));
                  const fnTaxSaving     = Math.round(annualTaxSaving / 26);
                  const realAnnualCost  = annualShortfall - annualTaxSaving;
                  return (
                    <div className="mt-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-600">Negative gearing tax saving</span>
                        <div className="flex gap-4 font-semibold text-emerald-700">
                          <span>+{fmt(annualTaxSaving)} / yr</span>
                          <span>+{fmt(fnTaxSaving)} / fn</span>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-neutral-500">
                        <span>Real cost after tax saving</span>
                        <span className="font-medium text-neutral-700">{fmt(realAnnualCost)} / yr</span>
                      </div>
                      <div className="mt-1.5 text-xs text-neutral-400">
                        At {investorMarginalRate}% marginal rate · Estimated only — actual saving depends on your full tax return.
                        {entityType === null && " Assumes personal ownership — assign a structure to see entity-specific tax treatment."}
                      </div>
                    </div>
                  );
                }
                // Trust, Company, SMSF — loss carry-forward
                const carryLabel = entityType === "Company" ? "Company loss carry-forward"
                  : entityType === "SMSF" ? "SMSF loss carry-forward"
                  : "Trust loss carry-forward";
                const carryNote = entityType === "Company"
                  ? "Losses inside a company are carried forward and offset future company income."
                  : entityType === "SMSF"
                  ? "Losses inside an SMSF are carried forward within the fund and cannot offset personal income."
                  : "Losses inside a trust cannot be offset against personal PAYG income. This loss is carried forward inside the trust and offsets future trust income when the property becomes positively geared.";
                return (
                  <div className="mt-2 rounded-xl bg-amber-50 px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-amber-800">{carryLabel}</span>
                      <span className="font-semibold text-amber-700">−{fmt(annualShortfall)} / yr</span>
                    </div>
                    <div className="mt-1 text-xs text-amber-700">{carryNote}</div>
                  </div>
                );
              })()}
              {/* Holding buffer inline edit */}
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-neutral-500 w-32 shrink-0">Holding Buffer</label>
                  <NumberInput
                    className="rounded-xl border border-neutral-200 px-3 py-2 text-sm w-48"
                    value={(draft.holdingBuffer ?? 0) === 0 ? "" : (draft.holdingBuffer ?? 0)}
                    onChange={(e) => setDraft((p) => ({ ...p, holdingBuffer: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0 }))}
                    onBlur={() => saveProperty()}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-neutral-500 w-32 shrink-0">Buffer start date</label>
                  <input
                    type="date"
                    className="rounded-xl border border-neutral-200 px-3 py-2 text-sm w-48"
                    value={draft.holdingBufferStartDate || new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDraft((p) => ({ ...p, holdingBufferStartDate: e.target.value }))}
                    onBlur={() => saveProperty()}
                  />
                </div>
              </div>

              {(() => {
                const bufferVal = draft.holdingBuffer ?? 0;
                const startDateVal = draft.holdingBufferStartDate ?? "";

                if (bufferVal > 0) {
                  const loanRate = draft.rate ?? 0;
                  const monthlyInterestSaving = (bufferVal * (loanRate / 100)) / 12;
                  const monthlyShortfall = netCashflowAnnual < 0 ? Math.abs(netCashflowAnnual) / 12 : 0;
                  const netMonthlyConsumption = monthlyShortfall - monthlyInterestSaving;
                  const monthsToRunOut = netMonthlyConsumption > 0
                    ? bufferVal / netMonthlyConsumption
                    : Infinity;

                  let bufferText = fmt(bufferVal);
                  if (monthlyInterestSaving > 0) {
                    bufferText += ` · $${Math.round(monthlyInterestSaving)}/mo interest saving`;
                  }

                  if (startDateVal) {
                    const startDate = new Date(startDateVal);
                    const startStr = startDate.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
                    bufferText += ` · Started ${startStr}`;
                  }

                  if (netCashflowAnnual >= 0) {
                    bufferText += " · Property is cashflow positive — buffer is not being consumed and contributes to offset savings";
                  } else if (startDateVal) {
                    const startDate = new Date(startDateVal);
                    const endDate = new Date(startDate);
                    endDate.setMonth(endDate.getMonth() + Math.round(monthsToRunOut));
                    const endStr = endDate.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
                    bufferText += ` · Runs out approx ${endStr}`;
                  } else {
                    bufferText += ` · covers ${(monthsToRunOut / 12).toFixed(1)} yrs at current shortfall`;
                  }

                  return (
                    <div className="mt-3 flex items-start justify-between rounded-xl border border-neutral-200 px-4 py-3 text-sm text-neutral-600">
                      <span className="shrink-0">Holding Buffer</span>
                      <span className="font-medium text-neutral-900 text-right ml-4">{bufferText}</span>
                    </div>
                  );
                }
                return null;
              })()}
            </CollapsibleSection>
          );
        })()}
      </div>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-neutral-900">
              Delete {draft.nickname}?
            </h2>
            <p className="mt-2 text-sm text-neutral-500">
              This will permanently remove the property and all its data. This action cannot be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  onDeleteProperty?.(property.id);
                  setDeleteModal(false);
                  onBack();
                }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
              >
                Delete permanently
              </button>
              <button
                onClick={() => setDeleteModal(false)}
                className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark as sold modal ───────────────────────────────────────────── */}
      {markSoldModal && (() => {
        // ATO s.110-45(3): only Div 43 (capital works) deductions reduce the
        // cost base. Div 40 (plant & equipment) goes through a separate
        // balancing adjustment, NOT the cost base. Entries marked "both" or
        // with no divisionType are excluded from this subtraction because we
        // can't know what portion is Div 43 — conservative for ATO compliance.
        const div43Claimed = (draft.depreciationClaimed ?? [])
          .filter((d) => d.divisionType === "43")
          .reduce((s, d) => s + d.amountClaimed, 0);
        // B130 Wave 2C Phase 9 — blocked Div 40 second-hand items also
        // reduce the cost base (s110-25 parallel logic). Their cost was
        // capitalised into property cost base on acquisition; on disposal
        // the unrecouped portion is removed from cost base. AusProp
        // simplification: use original `cost` (no decline accrued for
        // blocked items per Phase 3 calc helper which short-circuits with
        // adjustableValueAtDisposal=0).
        const div40PreviewItems = draft.depreciationSchedule?.div40Items ?? [];
        const div40PreviewPropertyContext: Div40EligibilityProperty = {
          assetClass: draft.assetClass,
          contractDate: draft.contractDate,
          purchaseDate: draft.purchaseDate,
          div43DateCompleted: draft.depreciationSchedule?.div43DateCompleted,
        };
        const div40PreviewOwnerStructure: Div40EligibilityOwnerStructure | null =
          entityStructure
            ? { excludedEntityType: entityStructure.excludedEntityType ?? null }
            : null;
        const div40PreviewBlockedReduction = div40PreviewItems
          .filter((i) => !i.poolAllocated)
          .reduce((s, item) => {
            const elig = computeDiv40Eligibility(item, div40PreviewPropertyContext, div40PreviewOwnerStructure);
            return s + (elig.eligible ? 0 : item.cost);
          }, 0);
        // Net Div 40 balancing adjustment for the preview (eligible items
        // only; pool write-off shown separately as a deduction). Reported
        // as a sibling line to gross profit per design §6 — NOT folded
        // into cost base or gross profit.
        const div40PreviewDisposalDate = saleFields.saleContractDate || saleFields.saleDate;
        const div40PreviewBalancingAdj = div40PreviewDisposalDate
          ? div40PreviewItems
              .filter((i) => !i.poolAllocated)
              .reduce((s, item) => {
                const elig = computeDiv40Eligibility(item, div40PreviewPropertyContext, div40PreviewOwnerStructure);
                if (!elig.eligible) return s;
                // B142 — implicit termination value (undefined) = AV → no event.
                if (item.terminationValue === undefined || item.terminationValue === null) return s;
                const result = computeDiv40BalancingAdjustment(item, elig, div40PreviewDisposalDate);
                return s + result.adjustment;
              }, 0)
          : 0;
        const costBase = (draft.purchasePrice ?? 0) + (draft.buyCosts ?? 0) + (draft.legalBuyCosts ?? 0)
          + (draft.capitalImprovements ?? []).reduce((s, ci) => s + ci.amount, 0)
          - div43Claimed
          - div40PreviewBlockedReduction;
        const saleCosts = saleFields.saleAgentFees + saleFields.saleLegalCosts + saleFields.saleOtherCosts;
        const grossProfit = (saleFields.salePrice || 0) - costBase - saleCosts;
        // B130 Wave 2A — CGT event date is the contract date (RPG 2025
        // p62), not settlement. Fall back to settlement (purchaseDate /
        // saleDate) when the contract dates aren't separately captured;
        // Phase 1 backfill makes that the same value for legacy rows.
        const purchaseContractDate = draft.contractDate || draft.purchaseDate || "";
        const saleContractDateLocal = saleFields.saleContractDate || saleFields.saleDate || "";
        const heldOver12 = isHeldOverTwelveMonths(purchaseContractDate, saleContractDateLocal);
        const previewDiscountRate = heldOver12 ? cgtDiscountRate(entityType) : 0;
        // Validation gate (Bug D fix) — Confirm requires non-empty saleDate
        // and a positive salePrice. Empty values would either fail NOT NULL
        // constraints on cgt_events or write garbage zeros.
        const canConfirm = !!saleFields.saleDate && (saleFields.salePrice || 0) > 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
              <h2 className="text-lg font-semibold text-neutral-900">Mark {draft.nickname} as sold</h2>
              <p className="mt-1 text-sm text-neutral-500">
                Record the sale details. When you click <strong>Confirm sale</strong>, three things happen: the property is archived to your History tab (not deleted, you can still view it), a permanent CGT event is recorded against this financial year, and the property is removed from your active portfolio totals. You can re-open the property from the History tab if you need to fix anything.
              </p>

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                <strong>This action records a permanent CGT event.</strong> Make sure your sale price, sale date, and selling costs are correct before confirming. The figures here flow into the Tax page CGT &amp; Cost Base tab and will appear on your end-of-year tax summary.
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* B130 Wave 2A Phase 6 — explicit sale contract date input.
                    Contract date is the CGT event date per RPG 2025 p62; sale
                    settlement is retained for ownership-period records. */}
                <div>
                  <label className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
                    Sale Contract Date
                    <InfoTooltip tip="The date the sale contract was signed. This is the CGT event date per ATO RPG 2025 p62 — determines the FY this gain is reported in and the 12-month discount eligibility. Often differs from settlement." />
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleContractDate}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleContractDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
                    Sale Settlement Date
                    <InfoTooltip tip="The date settlement occurred. Used for ownership-period and cashflow records. CGT event date is the contract date, not settlement." />
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleDate}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleDate: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-400">Sale price</label>
                  <NumberInput
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.salePrice || ""}
                    onChange={(e) => setSaleFields((p) => ({ ...p, salePrice: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
                    Agent fees
                    <InfoTooltip tip="Real estate agent commission paid on the sale. Subtracted from your sale proceeds when calculating gross profit. Include GST if you paid it (and were not entitled to claim it back). Typical range is 1.5%–3% of the sale price." />
                  </label>
                  <NumberInput
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleAgentFees || ""}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleAgentFees: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
                    Legal costs
                    <InfoTooltip tip="Conveyancing and legal fees paid to settle the sale. Subtracted from your sale proceeds when calculating gross profit. Do not include legal costs from the original purchase — those are already in the cost base from when you bought the property." />
                  </label>
                  <NumberInput
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleLegalCosts || ""}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleLegalCosts: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
                    Other costs
                    <InfoTooltip tip="Any other selling-related costs that reduce your sale proceeds — marketing, staging, building inspection requested by the buyer, mortgage discharge fee, settlement agent fees, etc. Do not include any costs already deducted against rental income (rates, insurance, repairs)." />
                  </label>
                  <NumberInput
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleOtherCosts || ""}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleOtherCosts: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-400">Notes (optional)</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={saleFields.saleNotes}
                    onChange={(e) => setSaleFields((p) => ({ ...p, saleNotes: e.target.value }))}
                    placeholder="e.g. Sold to upgrade"
                  />
                </div>
              </div>

              {/* B130 Wave 2C Phase 9 — Div 40 disposal section. Renders
                  when ≥1 div40Item is present. Per design §6: per-asset
                  AV + termination-value input + live adjustment, plus
                  pool write-off line. Blocked items routed to CGT cost-
                  base reduction (s110-25 parallel logic). Saved to
                  cgt_events.div40_* columns by the Confirm onClick. */}
              {(draft.depreciationSchedule?.div40Items?.length ?? 0) > 0 && (() => {
                const items = draft.depreciationSchedule!.div40Items!;
                const disposalDate = saleFields.saleContractDate || saleFields.saleDate;
                const propertyContext: Div40EligibilityProperty = {
                  assetClass: draft.assetClass,
                  contractDate: draft.contractDate,
                  purchaseDate: draft.purchaseDate,
                  div43DateCompleted: draft.depreciationSchedule?.div43DateCompleted,
                };
                // L40 / B106 SSOT: personal-name = null structure.
                const ownerStructure: Div40EligibilityOwnerStructure | null =
                  entityStructure
                    ? { excludedEntityType: entityStructure.excludedEntityType ?? null }
                    : null;

                const itemRows = items
                  .filter((i) => !i.poolAllocated)
                  .map((item) => {
                    const eligibility = computeDiv40Eligibility(item, propertyContext, ownerStructure);
                    const result = disposalDate
                      ? computeDiv40BalancingAdjustment(item, eligibility, disposalDate)
                      : null;
                    const defaultTv = result?.adjustableValueAtDisposal ?? 0;
                    const tvIsExplicit = item.terminationValue !== undefined && item.terminationValue !== null;
                    const terminationValue = tvIsExplicit ? item.terminationValue! : defaultTv;
                    // B142 fix — when the user hasn't explicitly set a
                    // termination value, the contract is "= AV → no event".
                    // computeDiv40BalancingAdjustment falls back to 0 internally
                    // (terminationValue ?? 0), producing a false −AV deduction
                    // line that flowed into the totals pre-fix.
                    const effectiveAdjustment = tvIsExplicit ? (result?.adjustment ?? 0) : 0;
                    return { item, eligibility, result, terminationValue, defaultTv, tvIsExplicit, effectiveAdjustment };
                  });

                const pools = recomputeLowValuePools(items);
                const disposalFy = disposalDate ? fyLabelFromDate(disposalDate) : null;
                const poolAtDisposal = disposalFy
                  ? pools.find((p) => p.fy === disposalFy)
                  : undefined;
                const poolWriteoff = poolAtDisposal
                  ? poolAtDisposal.openingBalance + poolAtDisposal.additionsThisFY
                  : 0;

                const incomeTotal = itemRows.reduce(
                  (s, r) => s + (r.eligibility.eligible && r.effectiveAdjustment > 0 ? r.effectiveAdjustment : 0),
                  0,
                );
                const itemDeductionTotal = itemRows.reduce(
                  (s, r) => s + (r.eligibility.eligible && r.effectiveAdjustment < 0 ? -r.effectiveAdjustment : 0),
                  0,
                );
                const deductionTotal = itemDeductionTotal + poolWriteoff;
                // Blocked items: cost flows to CGT cost-base reduction (s110-25).
                // computeDiv40BalancingAdjustment returns AV=0 for blocked
                // items by design — fall back to original cost (the cost
                // capitalised into property cost base on acquisition).
                const blockedCostBaseReduction = itemRows.reduce(
                  (s, r) => s + (r.eligibility.eligible
                    ? 0
                    : (r.result?.adjustableValueAtDisposal || r.item.cost)),
                  0,
                );

                return (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-900">
                      Div 40 disposal — assets and pool
                      <InfoTooltip tip="When you sell the property, depreciating assets undergo a 'balancing adjustment' per ITAA s40-285. If the sale-price portion you allocate to an asset (its termination value) exceeds its adjustable value, the excess is recaptured as income. If less, the shortfall is a deduction. Blocked second-hand assets don't trigger a balancing event — their remaining cost reduces the property's CGT cost base. Default termination values are set to adjustable value (zero balancing event); override per asset if your sale agreement allocates differently." />
                    </div>

                    <div className="space-y-2">
                      {itemRows.map(({ item, eligibility, result, terminationValue, defaultTv }) => (
                        <div key={item.id} className="rounded-lg bg-white p-2.5 text-xs">
                          <div className="flex items-baseline justify-between">
                            <span className="font-medium text-neutral-800">{item.name || "(unnamed)"}</span>
                            <span className="text-neutral-400">acquired {item.acquisitionDate ?? "—"}</span>
                          </div>
                          {!eligibility.eligible ? (
                            <div className="mt-1 text-amber-700">
                              Blocked — flows to CGT cost-base reduction {fmt(item.cost)}.
                            </div>
                          ) : (
                            <>
                              <div className="mt-1 flex items-baseline justify-between">
                                <span className="text-neutral-500">Adjustable value at disposal:</span>
                                <span>{fmt(result?.adjustableValueAtDisposal ?? 0)}</span>
                              </div>
                              <div className="mt-1 flex items-center gap-2">
                                <label className="text-neutral-500">Termination value:</label>
                                <NumberInput
                                  value={terminationValue === 0 ? "" : terminationValue}
                                  onChange={(e) => {
                                    const newItems = items.map((i) =>
                                      i.id === item.id
                                        ? { ...i, terminationValue: parseFloat(e.target.value) || 0 }
                                        : i,
                                    );
                                    setDraft({
                                      ...draft,
                                      depreciationSchedule: {
                                        ...draft.depreciationSchedule!,
                                        div40Items: newItems,
                                      },
                                    });
                                  }}
                                  onFocus={(e) => e.currentTarget.select()}
                                  placeholder={String(Math.round(defaultTv))}
                                  className="w-32 rounded border border-neutral-200 px-2 py-1 text-right text-xs"
                                />
                              </div>
                              <div className="mt-1 text-[11px] text-neutral-500">
                                {/* B142 fix — empty terminationValue defaults to AV
                                    (no balancing event), not 0. Pre-fix the empty
                                    input parsed to 0 → 0 − AV = −AV deduction
                                    line, which falsely surfaced a deduction the
                                    user never intended. */}
                                {!result && "Set sale date to compute adjustment."}
                                {result && (item.terminationValue === undefined || item.terminationValue === null) &&
                                  `Default: ${fmt(result.adjustableValueAtDisposal)} (= adjustable value, no balancing event).`}
                                {result && item.terminationValue !== undefined && item.terminationValue !== null && result.adjustment > 0 &&
                                  `+${fmt(result.adjustment)} income (recapture)`}
                                {result && item.terminationValue !== undefined && item.terminationValue !== null && result.adjustment < 0 &&
                                  `${fmt(result.adjustment)} deduction (residual decline)`}
                                {result && item.terminationValue !== undefined && item.terminationValue !== null && result.adjustment === 0 &&
                                  "No balancing event."}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {poolAtDisposal && poolWriteoff > 0 && (
                        <div className="rounded-lg bg-white p-2.5 text-xs">
                          <div className="font-medium text-neutral-800">Low-value pool</div>
                          <div className="mt-1 flex items-baseline justify-between">
                            <span className="text-neutral-500">Closing balance at disposal ({disposalFy}):</span>
                            <span>{fmt(poolWriteoff)}</span>
                          </div>
                          <div className="mt-1 text-emerald-700">
                            Fully deductible this FY (AusProp simplification of s40-440).
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 space-y-1 border-t border-amber-200 pt-2 text-xs">
                      {incomeTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Income (recapture):</span>
                          <span className="font-medium">+{fmt(incomeTotal)}</span>
                        </div>
                      )}
                      {deductionTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Deductions:</span>
                          <span className="font-medium text-emerald-700">−{fmt(deductionTotal)}</span>
                        </div>
                      )}
                      {blockedCostBaseReduction > 0 && (
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Blocked → CGT cost-base reduction:</span>
                          <span className="font-medium">−{fmt(blockedCostBaseReduction)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {saleFields.salePrice > 0 && (
                <div className="mt-4 rounded-xl bg-neutral-50 px-4 py-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Sale price</span>
                    <span className="font-medium">{fmt(saleFields.salePrice)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1 text-neutral-500">
                      Cost base (approx.)
                      <InfoTooltip tip="Your cost base is what you originally paid for the property plus any costs that increase its capital value, minus Div 43 capital works depreciation already claimed and minus the cost of any blocked second-hand Div 40 items (s110-25). Formula: purchase price + buy costs (stamp duty, conveyancing) + legal buy costs + capital improvements − Div 43 depreciation claimed − blocked Div 40 cost. Eligible Div 40 plant & equipment depreciation is NOT subtracted from the cost base — it goes through a separate balancing adjustment when the depreciating asset is disposed of (ATO s.110-45(3)). The cost base is subtracted from sale proceeds (after selling costs) to calculate your gross profit. Ongoing costs like rates, insurance, repairs and loan interest are NOT in the cost base — those have already been deducted against rental income each year." />
                    </span>
                    <span className="font-medium">{fmt(costBase)}</span>
                  </div>
                  {div40PreviewBlockedReduction > 0 && (
                    <div className="flex justify-between text-[11px] text-neutral-400">
                      <span className="pl-2">↳ includes blocked Div 40 reduction</span>
                      <span>−{fmt(div40PreviewBlockedReduction)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Sale costs</span>
                    <span className="font-medium">{fmt(saleCosts)}</span>
                  </div>
                  <div className={`flex justify-between border-t border-neutral-200 pt-1.5 font-semibold ${grossProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    <span>Gross profit</span>
                    <span>{grossProfit >= 0 ? "+" : ""}{fmt(grossProfit)}</span>
                  </div>
                  {div40PreviewBalancingAdj !== 0 && (
                    <div className="flex justify-between pt-0.5 text-[11px] text-neutral-500">
                      <span className="flex items-center gap-1">
                        Div 40 balancing adj (Other income/deduction)
                        <InfoTooltip tip="The Div 40 balancing adjustment is reported separately from the CGT gain — it's 'Other income' (when termination value > AV, recapture) or 'Other deduction' (when termination value < AV, residual decline). It does NOT enter the CGT-discount-eligible gross profit per ITAA s40-285 / s40-300." />
                      </span>
                      <span className={div40PreviewBalancingAdj > 0 ? "text-amber-700" : "text-emerald-700"}>
                        {div40PreviewBalancingAdj > 0 ? "+" : ""}{fmt(div40PreviewBalancingAdj)}
                      </span>
                    </div>
                  )}
                  {/* B130 Wave 2A — discount rate is entity-aware. Personal/Trust
                      = 50%, SMSF = 1/3, Company = 0%. The preview line shows
                      the rate that will actually apply on save. */}
                  {heldOver12 && previewDiscountRate > 0 && (
                    <div className="flex items-center gap-1 pt-0.5 text-[11px] text-neutral-500">
                      <span>
                        Held &gt;12 months — {previewDiscountRate === 0.5
                          ? "50% CGT discount"
                          : "33⅓% CGT discount (SMSF)"} applies.
                      </span>
                      <InfoTooltip tip="If you've held the property for more than 12 months, the ATO discounts the capital gain before tax: 50% for individuals and trusts (when distributed to individual beneficiaries), 33⅓% for SMSFs in accumulation phase, and 0% for companies (they pay full company tax). Capital losses are NEVER discounted — they carry forward at full value. Authority: ITAA 1997 s.115-25 (12-month rule), s.115-100 (50% rate), s.115-215 (trusts), s.295-385 (SMSF 1/3 rate). The Tax page CGT & Cost Base tab applies the same rate automatically." />
                    </div>
                  )}
                  {heldOver12 && previewDiscountRate === 0 && entityType === "Company" && (
                    <div className="flex items-center gap-1 pt-0.5 text-[11px] text-neutral-500">
                      <span>Held &gt;12 months — Company: no CGT discount applies.</span>
                      <InfoTooltip tip="Companies do not receive any CGT discount regardless of holding period. The full gain is taxed at the company tax rate. Authority: ITAA 1997 s.115-100 (50% rate restricted to individuals/trusts) and s.295-385 (1/3 for complying super funds)." />
                    </div>
                  )}
                </div>
              )}

              {cgtError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
                  <strong>Could not save CGT record:</strong> {cgtError}
                  <div className="mt-1 text-red-700">
                    The property has not been archived. You can retry, or continue without the CGT record (the property will still archive but no row will be added to cgt_events — you'll need to record the CGT event manually with your accountant).
                  </div>
                </div>
              )}

              {!canConfirm && (
                <p className="mt-3 text-xs text-amber-700">
                  Enter both a sale date and a sale price greater than $0 before confirming.
                </p>
              )}

              <div className="mt-5 flex gap-3">
                <button
                  disabled={!canConfirm || cgtSaving}
                  onClick={async () => {
                    setCgtError(null);

                    // Recompute everything fresh at click time so the saved
                    // payload exactly matches the preview the user just saw.
                    const div43ClaimedFinal = (draft.depreciationClaimed ?? [])
                      .filter((d) => d.divisionType === "43")
                      .reduce((s, d) => s + d.amountClaimed, 0);

                    // B130 Wave 2C Phase 9 — recompute Div 40 totals fresh
                    // for cgt_events.div40_* columns + cost-base reduction.
                    // Mirrors the Div 40 disposal section preview (above)
                    // by design — both the preview and the saved row read
                    // from the same SSOT helpers (computeDiv40Eligibility,
                    // computeDiv40BalancingAdjustment, recomputeLowValuePools).
                    const div40FinalItems: Div40Item[] = draft.depreciationSchedule?.div40Items ?? [];
                    const div40FinalPropertyContext: Div40EligibilityProperty = {
                      assetClass: draft.assetClass,
                      contractDate: draft.contractDate,
                      purchaseDate: draft.purchaseDate,
                      div43DateCompleted: draft.depreciationSchedule?.div43DateCompleted,
                    };
                    const div40FinalOwnerStructure: Div40EligibilityOwnerStructure | null =
                      entityStructure
                        ? { excludedEntityType: entityStructure.excludedEntityType ?? null }
                        : null;
                    const div40FinalDisposalDate =
                      saleFields.saleContractDate || saleFields.saleDate || "";
                    const div40FinalItemRows = div40FinalItems
                      .filter((i) => !i.poolAllocated)
                      .map((item) => {
                        const eligibility = computeDiv40Eligibility(
                          item,
                          div40FinalPropertyContext,
                          div40FinalOwnerStructure,
                        );
                        const result = computeDiv40BalancingAdjustment(
                          item,
                          eligibility,
                          div40FinalDisposalDate,
                        );
                        const tvIsExplicit =
                          item.terminationValue !== undefined && item.terminationValue !== null;
                        const terminationValue = tvIsExplicit
                          ? item.terminationValue!
                          : result.adjustableValueAtDisposal;
                        // B142 fix — implicit-default termination = AV → no
                        // event (adjustment 0). Avoids false −AV deduction
                        // recapture that would otherwise be persisted to
                        // cgt_events.div40_balancing_adjustment.
                        const effectiveAdjustment = tvIsExplicit ? result.adjustment : 0;
                        return { item, eligibility, result, terminationValue, tvIsExplicit, effectiveAdjustment };
                      });

                    const div40BalancingAdjustment = div40FinalItemRows.reduce(
                      (s, r) => s + (r.eligibility.eligible ? r.effectiveAdjustment : 0),
                      0,
                    );
                    const div40BlockedCostBaseReduction = div40FinalItemRows.reduce(
                      (s, r) => s + (!r.eligibility.eligible
                        ? (r.result.adjustableValueAtDisposal || r.item.cost)
                        : 0),
                      0,
                    );

                    const div40FinalPools = recomputeLowValuePools(div40FinalItems);
                    const div40FinalDisposalFy = div40FinalDisposalDate
                      ? fyLabelFromDate(div40FinalDisposalDate)
                      : null;
                    const div40FinalPoolAtDisposal = div40FinalDisposalFy
                      ? div40FinalPools.find((p) => p.fy === div40FinalDisposalFy)
                      : undefined;
                    const div40PoolWriteoff = div40FinalPoolAtDisposal
                      ? div40FinalPoolAtDisposal.openingBalance + div40FinalPoolAtDisposal.additionsThisFY
                      : 0;

                    // Classification for return routing.
                    const hasItems = div40FinalItemRows.length > 0;
                    const hasEligibleItems = div40FinalItemRows.some((r) => r.eligibility.eligible);
                    const hasBlockedItems = div40FinalItemRows.some((r) => !r.eligibility.eligible);
                    const hasIncome = div40FinalItemRows.some(
                      (r) => r.eligibility.eligible && r.effectiveAdjustment > 0,
                    );
                    const hasDeductionFromItems = div40FinalItemRows.some(
                      (r) => r.eligibility.eligible && r.effectiveAdjustment < 0,
                    );
                    const hasDeductionFromPool = div40PoolWriteoff > 0;
                    const div40BalancingType: string =
                      !hasItems && !hasDeductionFromPool ? "none"
                      : !hasEligibleItems && !hasDeductionFromPool && hasBlockedItems ? "cost-base-only"
                      : hasIncome && !hasDeductionFromItems && !hasDeductionFromPool ? "income"
                      : !hasIncome && (hasDeductionFromItems || hasDeductionFromPool) ? "deduction"
                      : "mixed";

                    const div40ItemsSummary = div40FinalItemRows.map((r) => ({
                      itemId: r.item.id,
                      name: r.item.name,
                      terminationValue: r.terminationValue,
                      adjustableValueAtDisposal: r.result.adjustableValueAtDisposal,
                      // B142 — persist the effective adjustment (0 when termination
                      // implicit) rather than result.adjustment (which the calc
                      // helper computed against terminationValue ?? 0).
                      adjustment: r.effectiveAdjustment,
                      type: r.tvIsExplicit ? r.result.type : "none",
                      reason: r.tvIsExplicit ? r.result.reason : "Implicit termination value = AV — no balancing event.",
                      eligible: r.eligibility.eligible,
                    }));

                    const cgtCostBase = (draft.purchasePrice ?? 0) + (draft.buyCosts ?? 0) + (draft.legalBuyCosts ?? 0)
                      + (draft.capitalImprovements ?? []).reduce((s, ci) => s + ci.amount, 0)
                      - div43ClaimedFinal
                      - div40BlockedCostBaseReduction;
                    const saleCostsTotal = (saleFields.saleAgentFees || 0) + (saleFields.saleLegalCosts || 0) + (saleFields.saleOtherCosts || 0);
                    const cgtGrossProfit = (saleFields.salePrice || 0) - cgtCostBase - saleCostsTotal;

                    const purchaseDateVal = draft.purchaseDate || null;
                    const saleDateVal = saleFields.saleDate || null;
                    // B130 Wave 2A — CGT event date is the contract date
                    // (RPG 2025 p62 / ITAA 1997 Div 115). Fall back to
                    // settlement when contract not separately captured.
                    const purchaseContractDateVal = draft.contractDate || draft.purchaseDate || null;
                    const saleContractDateVal = saleFields.saleContractDate || saleFields.saleDate || null;
                    const heldOver12 = isHeldOverTwelveMonths(
                      purchaseContractDateVal ?? "",
                      saleContractDateVal ?? "",
                    );
                    // B130 Wave 2A Phase 3 — entity-aware discount rate
                    // replaces the hard-coded 0.5/Personal-only path. SSOT
                    // is cgtDiscountRate(entityType) in lib/calculations.ts;
                    // Phase 4 wires it into TaxPage / buildPortfolioContext.
                    const discountRate = heldOver12 ? cgtDiscountRate(entityType) : 0;
                    const cgtDiscountApplied = discountRate > 0;
                    // Bug C fix preserved: capital losses are NEVER discounted.
                    // The discount applies only to gains; losses carry
                    // forward at full value (ITAA 1997 s.115-25 — discount
                    // is for "discount capital gains", not capital losses).
                    const taxableGain = cgtDiscountApplied && cgtGrossProfit > 0
                      ? cgtGrossProfit * (1 - discountRate)
                      : cgtGrossProfit;

                    // Bug E fix: try the cgt_events insert FIRST. Only archive
                    // the property locally + persist the property update if
                    // the insert succeeds. On failure, surface the error and
                    // let the user choose to retry or continue without the
                    // CGT record (separate button below).
                    if (supabase) {
                      try {
                        setCgtSaving(true);
                        const { data: { user } } = await supabase.auth.getUser();
                        if (!user) {
                          throw new Error("Not signed in — please refresh and try again.");
                        }
                        const { error: insertError } = await supabase.from('cgt_events').insert({
                          user_id: user.id,
                          property_id: draft.id || null,
                          property_address: [draft.address, draft.suburb, draft.state].filter(Boolean).join(', '),
                          entity_name: structureName || null,
                          entity_type: entityType || null,
                          purchase_date: purchaseDateVal,
                          sale_date: saleDateVal,
                          // B130 Wave 2A — record the CGT event date
                          // (contract) alongside the settlement date so
                          // future audits / FY allocation can use the
                          // legally-correct date per RPG 2025 p62.
                          contract_date: purchaseContractDateVal,
                          sale_contract_date: saleContractDateVal,
                          purchase_price: draft.purchasePrice || null,
                          sale_price: saleFields.salePrice,
                          cost_base: cgtCostBase,
                          sale_costs: saleCostsTotal,
                          gross_profit: cgtGrossProfit,
                          held_over_12_months: heldOver12,
                          cgt_discount_applied: cgtDiscountApplied,
                          // B130 Wave 2A — record the rate that was
                          // actually applied so the row is self-describing
                          // (Trust 0.5, SMSF 0.333…, Company 0.0).
                          discount_pct: discountRate,
                          taxable_gain: taxableGain,
                          notes: saleFields.saleNotes || null,
                          // B130 Wave 2C Phase 9 — Div 40 disposal record.
                          // Per design §6 + audit D13. NULLs preserve column
                          // optionality for legacy rows / properties without
                          // any Div 40 items.
                          div40_balancing_adjustment: div40BalancingAdjustment || null,
                          div40_balancing_type: div40BalancingType,
                          div40_pool_writeoff: div40PoolWriteoff || null,
                          div40_blocked_cost_base_reduction: div40BlockedCostBaseReduction || null,
                          div40_items_summary: div40ItemsSummary.length > 0 ? div40ItemsSummary : null,
                        });
                        if (insertError) {
                          throw new Error(insertError.message);
                        }
                      } catch (e) {
                        console.error('CGT event insert failed:', e);
                        setCgtError(e instanceof Error ? e.message : String(e));
                        setCgtSaving(false);
                        return;
                      }
                      setCgtSaving(false);
                    }

                    // CGT insert succeeded (or Supabase not configured) — now
                    // archive the property locally and persist the property
                    // record update.
                    const updated: PropertyRecord = {
                      ...draft,
                      status: "sold",
                      saleDate: saleFields.saleDate || undefined,
                      // B130 Wave 2A — persist the sale CGT event date.
                      // Falls back to saleDate so legacy rows stay round-trip
                      // safe through propertyToRow.
                      saleContractDate:
                        saleFields.saleContractDate || saleFields.saleDate || undefined,
                      salePrice: saleFields.salePrice || undefined,
                      saleAgentFees: saleFields.saleAgentFees || undefined,
                      saleLegalCosts: saleFields.saleLegalCosts || undefined,
                      saleOtherCosts: saleFields.saleOtherCosts || undefined,
                      saleNotes: saleFields.saleNotes || undefined,
                    };
                    setDraft(updated);
                    onSave(updated);

                    setMarkSoldModal(false);
                    onBack();
                  }}
                  className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {cgtSaving ? "Saving CGT event…" : "Confirm sale"}
                </button>
                {cgtError && (
                  <button
                    disabled={cgtSaving}
                    onClick={() => {
                      // Continue without CGT — archive locally and skip the
                      // CGT insert. User has acknowledged the failure.
                      setCgtError(null);
                      const updated: PropertyRecord = {
                        ...draft,
                        status: "sold",
                        saleDate: saleFields.saleDate || undefined,
                        // B130 Wave 2A — same fallback as the success branch
                        // so the local copy stays consistent with what the
                        // mapper would write.
                        saleContractDate:
                          saleFields.saleContractDate || saleFields.saleDate || undefined,
                        salePrice: saleFields.salePrice || undefined,
                        saleAgentFees: saleFields.saleAgentFees || undefined,
                        saleLegalCosts: saleFields.saleLegalCosts || undefined,
                        saleOtherCosts: saleFields.saleOtherCosts || undefined,
                        saleNotes: saleFields.saleNotes || undefined,
                      };
                      setDraft(updated);
                      onSave(updated);
                      setMarkSoldModal(false);
                      onBack();
                    }}
                    className="rounded-xl border border-red-200 bg-white px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-40"
                  >
                    Continue without CGT record
                  </button>
                )}
                <button
                  disabled={cgtSaving}
                  onClick={() => { setCgtError(null); setMarkSoldModal(false); }}
                  className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Documents slide-out ───────────────────────────────────────────── */}
      <DocumentsPanel
        open={showDocs}
        onClose={() => setShowDocs(false)}
        propertyId={draft.id}
        userId={userId}
        documents={draft.documents ?? []}
        changeLog={draft.changeLog ?? []}
        onUpload={(doc) => {
          const updated = { ...draft, documents: [...(draft.documents ?? []), doc] };
          setDraft(updated);
          onSave(updated);
        }}
        onUpdateDocTag={(id, tag) => {
          const updated = {
            ...draft,
            documents: (draft.documents ?? []).map((d) => d.id === id ? { ...d, tag } : d),
          };
          setDraft(updated);
          onSave(updated);
        }}
        onRemoveDoc={(id) => {
          const existing = (draft.documents ?? []).find((d) => d.id === id);
          if (existing?.filePath) void deleteFromStorage("property-documents", existing.filePath);
          const updated = {
            ...draft,
            documents: (draft.documents ?? []).filter((d) => d.id !== id),
          };
          setDraft(updated);
          onSave(updated);
        }}
      />

      {/* Email compose modal (N5 Stage 5) */}
      {emailNotice && (() => {
        const p = loadProfile();
        return (
          <EmailComposeModal
            item={emailNotice}
            property={draft}
            profile={p}
            teamContacts={p.teamContacts ?? []}
            onClose={() => setEmailNotice(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── Intelligence paywall teaser ──────────────────────────────────────────────

function IntelligencePaywall({ onUpgrade }: { onUpgrade?: () => void }) {
  return (
    <div className="relative mb-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      {/* Blurred preview layer */}
      <div className="pointer-events-none select-none blur-sm opacity-60 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-5 w-48 rounded-md bg-neutral-200" />
          <div className="h-5 w-24 rounded-full bg-amber-100" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["RCS Score", "Growth", "Demand"].map((label) => (
            <div key={label} className="rounded-xl bg-neutral-50 p-3 space-y-2">
              <div className="text-xs text-neutral-400">{label}</div>
              <div className="h-4 w-16 rounded bg-neutral-200" />
              <div className="h-2 w-full rounded-full bg-neutral-200">
                <div className="h-2 w-3/5 rounded-full bg-amber-300" />
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-neutral-50 p-4 space-y-2">
          <div className="h-3 w-full rounded bg-neutral-200" />
          <div className="h-3 w-5/6 rounded bg-neutral-200" />
          <div className="h-3 w-4/6 rounded bg-neutral-200" />
        </div>
        <div className="h-32 rounded-xl bg-neutral-50" />
      </div>

      {/* Lock overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] p-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neutral-900 mb-4">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-neutral-900">Market Intelligence</h3>
        <p className="mt-2 max-w-xs text-sm text-neutral-500 leading-relaxed">
          Suburb-level market scores, growth cycles, demand & supply analytics, and AI-powered insights — powered by HtAG Analytics.
        </p>
        <p className="mt-1.5 text-xs text-neutral-400">Available on the Investor plan</p>
        <button
          onClick={onUpgrade}
          className="mt-5 rounded-xl bg-neutral-900 px-6 py-3 text-sm font-semibold text-white hover:bg-neutral-700 transition-colors"
        >
          Upgrade to Investor — $39/month
        </button>
        <p className="mt-2 text-xs text-neutral-400">or $390/year · cancel anytime</p>
      </div>
    </div>
  );
}
